import { useState, useRef, useEffect } from 'react';
import { HelpCircle, X, ChevronRight, ExternalLink, Lightbulb, Link2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBlueprint } from '../contexts/BlueprintContext';
import { useHelpIcons } from '../contexts/HelpIconContext';

/**
 * HelpIcon - Contextual help component for KEY-MADE UI elements
 * 
 * Usage:
 * <HelpIcon
 *   elementId="budget-card"
 *   title="Project Budget"
 *   definition="Shows total project budget allocation"
 *   howToUse={["Click to view breakdown", "Edit in Budget Settings"]}
 *   relatedTo={[
 *     { feature: "Estimates", route: "/estimates", why: "Estimates feed into budget" },
 *     { feature: "Expenses", route: "/expenses", why: "Expenses deduct from budget" }
 *   ]}
 *   nextStep={{ action: "Create Estimate", route: "/estimates/new" }}
 *   blueprintId="budget-management"
 * />
 */

// Help content database - centralized definitions for all UI elements
export const HELP_CONTENT = {
  // Dashboard elements
  'focus-helper': {
    title: 'Focus Helper',
    definition: 'Shows your most urgent tasks that need attention today, prioritized by deadline and importance.',
    howToUse: [
      'Click "Start Focus" to begin working on a task',
      'Click "Done" to mark a task complete',
      'Click "Schedule" to reschedule for later'
    ],
    relatedTo: [
      { feature: 'Tasks', route: '/tasks', why: 'Tasks feed into Focus Helper' },
      { feature: 'Calendar', route: '/calendar', why: 'Scheduled tasks appear here' }
    ],
    nextStep: { action: 'Start your first focus session', event: 'startFocus' },
    blueprintId: 'productivity-tools'
  },
  'focus-time': {
    title: 'Focus Time',
    definition: 'Block dedicated time on your calendar for focused work without interruptions.',
    howToUse: [
      'Click "+" to add a new focus block',
      'Select duration and purpose',
      'Block syncs with your calendar'
    ],
    relatedTo: [
      { feature: 'Calendar', route: '/calendar', why: 'Focus blocks appear on calendar' },
      { feature: 'Time Tracking', route: '/time-tracking', why: 'Tracks time spent in focus' }
    ],
    nextStep: { action: 'Schedule focus time', event: 'addFocusTime' },
    blueprintId: 'productivity-tools'
  },
  'quick-upload': {
    title: 'Quick Upload',
    definition: 'Drag and drop files to quickly upload drawings, documents, or images to your projects.',
    howToUse: [
      'Drag files into the drop zone',
      'Files are automatically organized',
      'Supports PDF, PNG, JPG formats'
    ],
    relatedTo: [
      { feature: 'Floor Plans', route: '/floor-plans', why: 'Upload floor plans here' },
      { feature: 'Documents', route: '/documents', why: 'Upload contracts and specs' }
    ],
    nextStep: { action: 'Upload a file', event: 'uploadFile' },
    blueprintId: 'file-management'
  },
  'sales-pipeline': {
    title: 'Sales Pipeline',
    definition: 'Visual overview of your leads moving through the sales process from inquiry to won project.',
    howToUse: [
      'Click on any stage to see leads',
      'Drag leads between stages',
      'Click "+ New Lead" to add prospects'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', route: '/lead-pipeline', why: 'Full pipeline management' },
      { feature: 'Contacts', route: '/contacts', why: 'Leads become contacts' },
      { feature: 'Projects', route: '/projects', why: 'Won leads become projects' }
    ],
    nextStep: { action: 'Add a new lead', route: '/lead-pipeline?action=new' },
    blueprintId: 'crm-leads'
  },

  // Project Hub elements
  'project-status': {
    title: 'Project Status',
    definition: 'Current phase and overall health of your project at a glance.',
    howToUse: [
      'Click to change project status',
      'Green = on track, Yellow = needs attention, Red = blocked'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Status reflects journey progress' },
      { feature: 'Workflow Progress', why: 'Tied to workflow completion' }
    ],
    nextStep: { action: 'Review project progress', event: 'openJourney' },
    blueprintId: 'project-management'
  },
  'total-items': {
    title: 'Total Items',
    definition: 'Count of all rooms, areas, and items in your project scope.',
    howToUse: [
      'Click to view item breakdown',
      'Items come from floor plan takeoffs',
      'Each item can have materials assigned'
    ],
    relatedTo: [
      { feature: 'Floor Plans', why: 'Items defined from floor plans' },
      { feature: 'Clips', why: 'Materials assigned to items' },
      { feature: 'Estimates', why: 'Items appear in estimates' }
    ],
    nextStep: { action: 'View all items', event: 'viewItems' },
    blueprintId: 'takeoff-tools'
  },
  'project-budget': {
    title: 'Project Budget',
    definition: 'Total allocated budget and current spending status for this project.',
    howToUse: [
      'Click to view budget breakdown',
      'Compare estimates vs actuals',
      'Set budget alerts'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Estimates define initial budget' },
      { feature: 'Expenses', why: 'Expenses track actual spending' },
      { feature: 'Invoices', why: 'Invoices track client payments' },
      { feature: 'Purchase Orders', why: 'POs commit budget' }
    ],
    nextStep: { action: 'Create an estimate', route: 'estimates' },
    blueprintId: 'financials-budget'
  },
  'workflow-progress': {
    title: 'Workflow Progress',
    definition: 'Visual timeline showing your progress through the design phases.',
    howToUse: [
      'Click any phase to jump to it',
      'Green phases are complete',
      'Current phase is highlighted',
      'Click "Next" to see suggested next step'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Journey tracks detailed steps' },
      { feature: 'Compass', why: 'Compass guides next actions' }
    ],
    nextStep: { action: 'Open Compass for guidance', event: 'openCompass' },
    blueprintId: 'project-workflow'
  },
  'journey-progress': {
    title: 'Journey Progress',
    definition: 'Detailed progress tracker for all steps in your project workflow.',
    howToUse: [
      'Shows completion percentage',
      'Click "Full View" for detailed steps',
      'Next steps are highlighted'
    ],
    relatedTo: [
      { feature: 'Workflow Progress', why: 'Summary of journey' },
      { feature: 'Compass', why: 'Compass shows current step' }
    ],
    nextStep: { action: 'View full journey', event: 'openJourney' },
    blueprintId: 'project-workflow'
  },
  'project-images': {
    title: 'Project Images',
    definition: 'Main project image displayed across KEY-MADE dashboards and client portals.',
    howToUse: [
      'Click "Set Main Image" to upload',
      'Click "Open Gallery" for all images',
      'Drag to reorder gallery'
    ],
    relatedTo: [
      { feature: 'Drawing Room', why: 'Shows in client portal' },
      { feature: 'Site Photos', why: 'Site photos in gallery' }
    ],
    nextStep: { action: 'Set main image', event: 'setMainImage' },
    blueprintId: 'project-media'
  },

  // Financial elements
  'estimate-card': {
    title: 'Estimate',
    definition: 'Detailed cost breakdown for client approval, including materials, labor, and fees.',
    howToUse: [
      'Click to view/edit estimate',
      'Add line items from clips',
      'Send to client for approval',
      'Convert to invoice when approved'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Materials come from clips' },
      { feature: 'RFPs', why: 'Send RFPs to contractors' },
      { feature: 'Invoices', why: 'Estimates become invoices' },
      { feature: 'Budget', why: 'Estimates define budget' }
    ],
    nextStep: { action: 'Create new estimate', route: 'estimates/new' },
    blueprintId: 'financials-estimates'
  },
  'invoice-card': {
    title: 'Invoice',
    definition: 'Billing document sent to clients for payment of services or materials.',
    howToUse: [
      'Create from estimate or manually',
      'Track payment status',
      'Send reminders for unpaid invoices',
      'Record payments when received'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Invoices from estimates' },
      { feature: 'Payments', why: 'Track payment against invoice' },
      { feature: 'Budget', why: 'Invoices track revenue' }
    ],
    nextStep: { action: 'Create invoice', route: '/invoices/new' },
    blueprintId: 'financials-invoices'
  },
  'expense-card': {
    title: 'Expense',
    definition: 'Track project costs including materials, subcontractors, and overhead.',
    howToUse: [
      'Log expenses as they occur',
      'Attach receipts',
      'Categorize for reporting',
      'Link to purchase orders'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'POs track committed expenses' },
      { feature: 'Budget', why: 'Expenses reduce budget' },
      { feature: 'Reports', why: 'Appear in financial reports' }
    ],
    nextStep: { action: 'Log expense', event: 'openExpenseDialog' },
    blueprintId: 'financials-expenses'
  },
  'purchase-order-card': {
    title: 'Purchase Order',
    definition: 'Formal order to a vendor for materials or services with agreed pricing.',
    howToUse: [
      'Create from bid or manually',
      'Track delivery status',
      'Receive items when delivered',
      'Match to vendor invoices'
    ],
    relatedTo: [
      { feature: 'Bids', why: 'POs from awarded bids' },
      { feature: 'Vendors', why: 'POs sent to vendors' },
      { feature: 'Receiving', why: 'Track deliveries' },
      { feature: 'Expenses', why: 'POs become expenses' }
    ],
    nextStep: { action: 'Create purchase order', route: '/procurement' },
    blueprintId: 'financials-procurement'
  },

  // Clips elements
  'clip-card': {
    title: 'Material Clip',
    definition: 'Saved product or material with specifications, images, and pricing for project use.',
    howToUse: [
      'Click to view full details',
      'Add to estimate or specification',
      'Request quotes from vendors',
      'Compare similar products'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Clips add to estimates' },
      { feature: 'Specifications', why: 'Clips build spec sheets' },
      { feature: 'Vendors', why: 'Request pricing from vendors' }
    ],
    nextStep: { action: 'Add to project', event: 'addClipToProject' },
    blueprintId: 'clips-library'
  },

  // Contact elements
  'contact-card': {
    title: 'Contact',
    definition: 'Person or company in your network - clients, contractors, vendors, or team members.',
    howToUse: [
      'Click to view full profile',
      'Send email or call directly',
      'View associated projects',
      'Add notes and tags'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Contacts linked to projects' },
      { feature: 'Emails', why: 'Communication history' },
      { feature: 'Calendar', why: 'Meetings with contacts' }
    ],
    nextStep: { action: 'View contact details', event: 'viewContact' },
    blueprintId: 'crm-contacts'
  },

  // ============================================
  // LEFT SIDEBAR NAVIGATION ITEMS
  // ============================================
  'nav-my-desk': {
    title: 'My Desk',
    definition: 'Your personal dashboard showing tasks, events, and quick actions for today.',
    howToUse: [
      'View your daily tasks and priorities',
      'See upcoming calendar events',
      'Quick access to recent projects'
    ],
    relatedTo: [
      { feature: 'Tasks', why: 'Shows your assigned tasks' },
      { feature: 'Calendar', why: 'Shows today\'s schedule' }
    ],
    blueprintId: 'dashboard'
  },
  'nav-lead-pipeline': {
    title: 'Lead Pipeline',
    definition: 'Manage prospective clients from first inquiry through to signed contract.',
    howToUse: [
      'Add new leads from inquiries',
      'Move leads through stages',
      'Convert won leads to projects'
    ],
    relatedTo: [
      { feature: 'Contacts', why: 'Leads become contacts' },
      { feature: 'Projects', why: 'Won leads become projects' }
    ],
    blueprintId: 'crm-leads'
  },
  'nav-projects': {
    title: 'Projects',
    definition: 'All your active and archived design projects in one place.',
    howToUse: [
      'View projects by phase',
      'Click to open project hub',
      'Filter by status or client'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Projects come from won leads' },
      { feature: 'Financials', why: 'Each project has budgets' }
    ],
    blueprintId: 'project-management'
  },
  'nav-tasks': {
    title: 'Tasks',
    definition: 'All your tasks across all projects, organized by status and deadline.',
    howToUse: [
      'Create and assign tasks',
      'Set due dates and priorities',
      'Track completion status'
    ],
    relatedTo: [
      { feature: 'Focus Helper', why: 'Urgent tasks shown in Focus' },
      { feature: 'Calendar', why: 'Tasks with dates appear here' }
    ],
    blueprintId: 'productivity-tools'
  },
  'nav-calendar': {
    title: 'Calendar',
    definition: 'Schedule and view meetings, site visits, deadlines, and milestones.',
    howToUse: [
      'Schedule meetings with clients',
      'View project milestones',
      'Sync with external calendars'
    ],
    relatedTo: [
      { feature: 'Tasks', why: 'Task deadlines appear here' },
      { feature: 'Contacts', why: 'Meetings with contacts' }
    ],
    blueprintId: 'scheduling'
  },
  'nav-time-tracking': {
    title: 'Time Tracking',
    definition: 'Log time spent on projects for billing, productivity, and resource planning.',
    howToUse: [
      'Start/stop timer while working',
      'Log time manually',
      'Review time by project'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Time linked to projects' },
      { feature: 'Invoices', why: 'Bill clients for time' }
    ],
    blueprintId: 'productivity-tools'
  },
  'nav-notes': {
    title: 'Notes',
    definition: 'Quick notes, memos, and documentation across all your projects.',
    howToUse: [
      'Create quick notes',
      'Attach to projects',
      'Search past notes'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Notes linked to projects' },
      { feature: 'Quick Memo', why: 'Voice memos saved here' }
    ],
    blueprintId: 'documentation'
  },
  'nav-work-table': {
    title: 'Work Table',
    definition: 'Customizable spreadsheet-style view of your data for bulk editing.',
    howToUse: [
      'Edit multiple items at once',
      'Filter and sort data',
      'Export to spreadsheet'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'View project data' },
      { feature: 'Contacts', why: 'View contact data' }
    ],
    blueprintId: 'data-management'
  },
  'nav-clip-library': {
    title: 'Clip Library',
    definition: 'Your collection of saved products, materials, and finishes with pricing.',
    howToUse: [
      'Browse saved clips',
      'Add clips to projects',
      'Organize with tags'
    ],
    relatedTo: [
      { feature: 'Clipper Extension', why: 'Clips saved from web' },
      { feature: 'Estimates', why: 'Clips used in estimates' }
    ],
    blueprintId: 'clips-library'
  },
  'nav-paint-library': {
    title: 'Paint Library',
    definition: 'Collection of paint colors with brand info, codes, and project usage.',
    howToUse: [
      'Browse paint colors',
      'Add to spec sheets',
      'Track usage across projects'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Paints are a type of clip' },
      { feature: 'Specifications', why: 'Paint specs for projects' }
    ],
    blueprintId: 'clips-library'
  },
  'nav-contacts': {
    title: 'Contacts',
    definition: 'Your directory of clients, contractors, vendors, and team members.',
    howToUse: [
      'Add new contacts',
      'Import from business cards',
      'Search and filter'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Contacts linked to projects' },
      { feature: 'Lead Pipeline', why: 'Leads become contacts' }
    ],
    blueprintId: 'crm-contacts'
  },

  // ============================================
  // REPORTING & SUMMARY WIDGETS
  // ============================================
  'report-revenue': {
    title: 'Revenue',
    definition: 'Total invoiced amount from all projects, showing income trends.',
    howToUse: [
      'View by period (month/quarter/year)',
      'Click for detailed breakdown',
      'Compare to previous periods'
    ],
    relatedTo: [
      { feature: 'Invoices', why: 'Revenue from invoices' },
      { feature: 'Projects', why: 'Revenue by project' }
    ],
    dataSource: 'Calculated from all paid and pending invoices',
    blueprintId: 'financials-reports'
  },
  'report-expenses': {
    title: 'Total Expenses',
    definition: 'Sum of all project costs including materials, labor, and overhead.',
    howToUse: [
      'View by category',
      'Compare to budget',
      'Identify cost overruns'
    ],
    relatedTo: [
      { feature: 'Expenses', why: 'Individual expense entries' },
      { feature: 'Purchase Orders', why: 'Committed costs' }
    ],
    dataSource: 'Sum of all logged expenses and received POs',
    blueprintId: 'financials-reports'
  },
  'report-profit': {
    title: 'Profit/Loss',
    definition: 'Net profit calculated as revenue minus expenses across all projects.',
    howToUse: [
      'Track monthly profit trends',
      'View by project for profitability',
      'Set profit targets'
    ],
    relatedTo: [
      { feature: 'Revenue', why: 'Income side of equation' },
      { feature: 'Expenses', why: 'Cost side of equation' }
    ],
    dataSource: 'Revenue (invoices) minus Expenses (costs)',
    blueprintId: 'financials-reports'
  },
  'report-outstanding': {
    title: 'Outstanding Balance',
    definition: 'Total amount owed by clients from unpaid or partially paid invoices.',
    howToUse: [
      'View aging by days overdue',
      'Click to see unpaid invoices',
      'Send payment reminders'
    ],
    relatedTo: [
      { feature: 'Invoices', why: 'Shows unpaid invoices' },
      { feature: 'Clients', why: 'Amount owed by client' }
    ],
    dataSource: 'Sum of unpaid invoice balances',
    blueprintId: 'financials-invoices'
  },
  'report-committed': {
    title: 'Committed Costs',
    definition: 'Total value of open purchase orders not yet received or invoiced.',
    howToUse: [
      'View pending deliveries',
      'Factor into budget planning',
      'Track vendor orders'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Open PO values' },
      { feature: 'Budget', why: 'Future expenses' }
    ],
    dataSource: 'Sum of open purchase order amounts',
    blueprintId: 'financials-procurement'
  },
  'report-active-projects': {
    title: 'Active Projects',
    definition: 'Count of projects currently in progress (not completed or archived).',
    howToUse: [
      'Click to view project list',
      'Monitor workload capacity',
      'Track by phase'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'List of active projects' }
    ],
    dataSource: 'Count of projects with status = active',
    blueprintId: 'project-management'
  },
  'report-pending-bids': {
    title: 'Pending Bids',
    definition: 'Number of RFPs sent to contractors awaiting bid responses.',
    howToUse: [
      'Follow up on overdue bids',
      'Compare received bids',
      'Award winning bids'
    ],
    relatedTo: [
      { feature: 'RFPs', why: 'Sent bid requests' },
      { feature: 'Contractors', why: 'Vendors being bid' }
    ],
    dataSource: 'Count of RFPs with status = pending',
    blueprintId: 'financials-bids'
  },
  'report-deliveries': {
    title: 'Pending Deliveries',
    definition: 'Purchase orders that have been sent but items not yet received.',
    howToUse: [
      'Track delivery dates',
      'Follow up with vendors',
      'Mark items as received'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Orders awaiting delivery' },
      { feature: 'Receiving', why: 'Mark as received' }
    ],
    dataSource: 'Count of POs with status = ordered',
    blueprintId: 'financials-procurement'
  },

  // ============================================
  // PROCUREMENT WIDGETS
  // ============================================
  'procurement-summary': {
    title: 'Procurement Summary',
    definition: 'Overview of all purchasing activity including orders, bids, and deliveries.',
    howToUse: [
      'View order status at a glance',
      'Track spending by vendor',
      'Monitor delivery schedule'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'All POs listed here' },
      { feature: 'Bids', why: 'Bids lead to POs' }
    ],
    dataSource: 'Aggregated from purchase orders, RFPs, and receiving',
    blueprintId: 'financials-procurement'
  },
  'procurement-orders': {
    title: 'Orders Summary',
    definition: 'Breakdown of purchase orders by status (draft, sent, received, complete).',
    howToUse: [
      'Click status to filter',
      'View order details',
      'Process pending orders'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Individual orders' }
    ],
    dataSource: 'Count and value of POs grouped by status',
    blueprintId: 'financials-procurement'
  },

  // ============================================
  // FINANCIAL SUMMARY WIDGETS
  // ============================================
  'financial-summary': {
    title: 'Financial Summary',
    definition: 'High-level overview of all financial metrics across your projects.',
    howToUse: [
      'View key financial KPIs',
      'Click any metric for details',
      'Compare to previous periods'
    ],
    relatedTo: [
      { feature: 'Invoices', why: 'Revenue data' },
      { feature: 'Expenses', why: 'Cost data' },
      { feature: 'Budget', why: 'Budget tracking' }
    ],
    dataSource: 'Aggregated from all financial transactions',
    blueprintId: 'financials-reports'
  },

  // ============================================
  // PROJECT HUB SECTIONS
  // ============================================
  'project-info': {
    title: 'Project Info',
    definition: 'Basic project details including name, address, client, and project type.',
    howToUse: [
      'Click edit to update details',
      'View client contact info',
      'Change project status'
    ],
    relatedTo: [
      { feature: 'Client', why: 'Project owner' },
      { feature: 'Team', why: 'Assigned members' }
    ],
    blueprintId: 'project-management'
  },
  'site-conditions': {
    title: 'Site Conditions',
    definition: 'Physical characteristics of the project site that affect design decisions.',
    howToUse: [
      'Document site constraints',
      'Record measurements',
      'Note existing conditions'
    ],
    relatedTo: [
      { feature: 'Floor Plans', why: 'Site dimensions' },
      { feature: 'Design', why: 'Design constraints' }
    ],
    blueprintId: 'site-documentation'
  },
  'project-schedule': {
    title: 'Schedule',
    definition: 'Project timeline showing phases, milestones, and key dates.',
    howToUse: [
      'View Gantt chart timeline',
      'Set milestone dates',
      'Track progress'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Phase completion' },
      { feature: 'Calendar', why: 'Key dates' }
    ],
    blueprintId: 'project-scheduling'
  },
  'project-actions': {
    title: 'Action Items',
    definition: 'Specific tasks and to-dos for this project with assignments and due dates.',
    howToUse: [
      'Add new action items',
      'Assign to team members',
      'Mark complete when done'
    ],
    relatedTo: [
      { feature: 'Tasks', why: 'Part of task system' },
      { feature: 'Team', why: 'Assigned to members' }
    ],
    blueprintId: 'project-management'
  },
  'project-progress': {
    title: 'Progress Log',
    definition: 'Historical record of project progress updates and status changes.',
    howToUse: [
      'Add progress entries',
      'Attach photos',
      'Track milestone completion'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Linked to workflow' },
      { feature: 'Client Portal', why: 'Shared with client' }
    ],
    blueprintId: 'project-management'
  },
  'project-meetings': {
    title: 'Meetings',
    definition: 'Scheduled meetings and appointments for this project.',
    howToUse: [
      'Schedule client meetings',
      'Set reminders',
      'Add meeting notes'
    ],
    relatedTo: [
      { feature: 'Calendar', why: 'Shows in calendar' },
      { feature: 'Contacts', why: 'Meeting participants' }
    ],
    blueprintId: 'scheduling'
  },
  'project-team': {
    title: 'Team',
    definition: 'People assigned to this project including roles and contact info.',
    howToUse: [
      'Add team members',
      'Assign roles',
      'View contact details'
    ],
    relatedTo: [
      { feature: 'Contacts', why: 'Team from contacts' },
      { feature: 'Tasks', why: 'Assign tasks to team' }
    ],
    blueprintId: 'project-management'
  },
  'project-emails': {
    title: 'Emails',
    definition: 'Email history and templates for communicating with project stakeholders.',
    howToUse: [
      'View sent emails',
      'Use templates',
      'Schedule emails'
    ],
    relatedTo: [
      { feature: 'Contacts', why: 'Email recipients' },
      { feature: 'Journey', why: 'Triggered by workflow' }
    ],
    blueprintId: 'communication'
  },
  'project-floor-plans': {
    title: 'Floor Plans',
    definition: 'Uploaded floor plan drawings for measurement and annotation.',
    howToUse: [
      'Upload PDF floor plans',
      'Use Takeoff tool for measurements',
      'Define rooms and areas'
    ],
    relatedTo: [
      { feature: 'Takeoff', why: 'Measure from plans' },
      { feature: 'Rooms', why: 'Define room boundaries' }
    ],
    blueprintId: 'takeoff-tools'
  },
  'project-clips': {
    title: 'Project Clips',
    definition: 'Materials and products selected for this specific project.',
    howToUse: [
      'Add clips from library',
      'Mark as in proposal',
      'Add to estimates'
    ],
    relatedTo: [
      { feature: 'Clip Library', why: 'Source of clips' },
      { feature: 'Estimates', why: 'Clips in estimates' }
    ],
    blueprintId: 'clips-library'
  },
  'project-scope': {
    title: 'Scope',
    definition: 'Defined scope of work including all items to be completed.',
    howToUse: [
      'Define scope items',
      'Assign to rooms',
      'Generate scope document'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Scope becomes estimate' },
      { feature: 'Contracts', why: 'Scope in contracts' }
    ],
    blueprintId: 'scope-management'
  },
  'project-bids': {
    title: 'Bids',
    definition: 'Request for proposals sent to contractors and their responses.',
    howToUse: [
      'Create and send RFPs',
      'Compare received bids',
      'Award winning bid'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Bid recipients' },
      { feature: 'Purchase Orders', why: 'Awarded bids become POs' }
    ],
    blueprintId: 'financials-bids'
  },
  'project-estimates': {
    title: 'Estimates',
    definition: 'Cost estimates and proposals for client approval.',
    howToUse: [
      'Create detailed estimates',
      'Send to client',
      'Track approval status'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Materials in estimate' },
      { feature: 'Invoices', why: 'Estimates become invoices' }
    ],
    blueprintId: 'financials-estimates'
  },

  // ============================================
  // IN PROPOSAL STATUS
  // ============================================
  'in-proposal': {
    title: 'In Proposal',
    definition: 'Items marked as ready to be included in client estimates or proposals.',
    howToUse: [
      'Mark clips as "In Proposal"',
      'Generate estimate from these items',
      'Track what\'s been proposed'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Source of proposal items' },
      { feature: 'Estimates', why: 'Items included in estimates' }
    ],
    dataSource: 'Count of clips with in_proposal = true',
    blueprintId: 'financials-estimates'
  },

  // ============================================
  // CALENDAR WIDGET
  // ============================================
  'calendar-widget': {
    title: 'Calendar Widget',
    definition: 'Quick view of upcoming events and scheduled items.',
    howToUse: [
      'See today\'s schedule',
      'Click to add events',
      'Navigate by date'
    ],
    relatedTo: [
      { feature: 'Calendar', why: 'Full calendar view' },
      { feature: 'Tasks', why: 'Task deadlines shown' }
    ],
    blueprintId: 'scheduling'
  },

  // ============================================
  // ACTIVITY TIMELINE
  // ============================================
  'activity-timeline': {
    title: 'Activity Timeline',
    definition: 'Chronological feed of all actions and changes across your projects.',
    howToUse: [
      'See recent activity',
      'Filter by project or type',
      'Click to view details'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Activity by project' },
      { feature: 'Team', why: 'See team actions' }
    ],
    dataSource: 'Real-time log of all system events',
    blueprintId: 'activity-tracking'
  },

  // ============================================
  // LEAD PIPELINE STATS
  // ============================================
  'pipeline-total-leads': {
    title: 'Total Leads',
    definition: 'Total count of all leads in your pipeline regardless of status.',
    howToUse: [
      'Click to view all leads',
      'Includes active, won, and lost leads',
      'Add new leads with + button'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Full pipeline view' },
      { feature: 'Contacts', why: 'Leads become contacts' }
    ],
    dataSource: 'Count of all leads in pipeline collection',
    blueprintId: 'crm-leads'
  },
  'pipeline-active': {
    title: 'Active Leads',
    definition: 'Leads currently being worked - not yet won or lost.',
    howToUse: [
      'Click to filter to active only',
      'Focus outreach on these leads',
      'Move through stages to progress'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Active stage leads' },
      { feature: 'Tasks', why: 'Follow-up tasks' }
    ],
    dataSource: 'Count of leads with status = inquiry, consultation, proposal, or closing',
    blueprintId: 'crm-leads'
  },
  'pipeline-won': {
    title: 'Won Leads',
    definition: 'Leads that have been successfully converted to projects.',
    howToUse: [
      'Click to see won leads',
      'Celebrate wins!',
      'Create projects from won leads'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Won leads become projects' }
    ],
    dataSource: 'Count of leads with status = won',
    blueprintId: 'crm-leads'
  },
  'pipeline-conversion': {
    title: 'Conversion Rate',
    definition: 'Percentage of leads that convert from inquiry to won project.',
    howToUse: [
      'Track over time to improve',
      'Industry average is 20-30%',
      'Identify bottlenecks by stage'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Stage conversion analysis' }
    ],
    dataSource: '(Won leads / Total leads that reached final stages) × 100',
    blueprintId: 'crm-reports'
  },
  'pipeline-value': {
    title: 'Pipeline Value',
    definition: 'Total estimated value of all active leads in your pipeline.',
    howToUse: [
      'Forecast future revenue',
      'Prioritize high-value leads',
      'Set pipeline goals'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Sum of lead values' },
      { feature: 'Revenue', why: 'Projected future revenue' }
    ],
    dataSource: 'Sum of estimated_value from all active leads',
    blueprintId: 'crm-reports'
  },

  // ============================================
  // PROJECTS LIST
  // ============================================
  'projects-list': {
    title: 'All Projects',
    definition: 'Overview of all your design projects organized by phase.',
    howToUse: [
      'Click a project to open its hub',
      'Filter by phase using tabs',
      'Create new project with + button'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Won leads become projects' },
      { feature: 'Project Hub', why: 'Individual project details' }
    ],
    blueprintId: 'project-management'
  },
  'project-card': {
    title: 'Project Card',
    definition: 'Quick summary of a project showing phase, client, and recent activity.',
    howToUse: [
      'Click to open project hub',
      'See phase badge at top',
      'Quick actions via menu button'
    ],
    relatedTo: [
      { feature: 'Project Hub', why: 'Full project details' },
      { feature: 'Client', why: 'Project owner info' }
    ],
    blueprintId: 'project-management'
  },

  // ============================================
  // TASKS PAGE
  // ============================================
  'tasks-list': {
    title: 'Tasks',
    definition: 'All your tasks across all projects, organized by status.',
    howToUse: [
      'Filter by status or project',
      'Click to view/edit task',
      'Mark complete when done'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Tasks linked to projects' },
      { feature: 'Focus Helper', why: 'Urgent tasks shown there' }
    ],
    blueprintId: 'productivity-tools'
  },
  'task-card': {
    title: 'Task',
    definition: 'Individual action item with due date, priority, and assignment.',
    howToUse: [
      'Click checkbox to complete',
      'Click title to edit',
      'Drag to reorder priority'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Task belongs to project' },
      { feature: 'Team', why: 'Assigned to team member' }
    ],
    blueprintId: 'productivity-tools'
  },

  // ============================================
  // FLOOR PLAN TAKEOFF TOOLS - CRITICAL WORKFLOW
  // ============================================
  'takeoff-page': {
    title: 'Floor Plan Takeoff',
    definition: 'CAD-like measurement tool for extracting quantities and dimensions from floor plan PDFs. This is where you define rooms, measure areas, and mark product placements.',
    howToUse: [
      '1. Upload a floor plan PDF',
      '2. Calibrate the scale using a known dimension',
      '3. Use polygon tool to define room boundaries',
      '4. Use line tool for linear measurements',
      '5. Use selection tool to mark product placements'
    ],
    relatedTo: [
      { feature: 'Floor Plans', route: '/floor-plans', why: 'Upload plans here' },
      { feature: 'Clips', why: 'Assign materials to selections' },
      { feature: 'Estimates', why: 'Quantities feed into estimates' },
      { feature: 'Scope', why: 'Room definitions create scope' }
    ],
    nextStep: { action: 'Upload a floor plan', event: 'uploadPlan' },
    blueprintId: 'takeoff-tools'
  },
  'takeoff-calibration': {
    title: 'Calibration Tool',
    definition: 'Set the scale of your floor plan by measuring a known dimension (like a door width or wall length).',
    howToUse: [
      '1. Click the calibration tool',
      '2. Draw a line along a known dimension',
      '3. Enter the real-world measurement',
      '4. Scale is now set for all measurements'
    ],
    relatedTo: [
      { feature: 'Measurements', why: 'Calibration enables accurate measurements' }
    ],
    blueprintId: 'takeoff-tools'
  },
  'takeoff-polygon': {
    title: 'Room Polygon Tool',
    definition: 'Draw polygons to define room boundaries and calculate square footage.',
    howToUse: [
      '1. Click to place corner points',
      '2. Double-click to close the polygon',
      '3. Area is automatically calculated',
      '4. Name the room for scope tracking'
    ],
    relatedTo: [
      { feature: 'Rooms', why: 'Creates room definitions' },
      { feature: 'Scope', why: 'Rooms become scope items' },
      { feature: 'Estimates', why: 'Room areas in estimates' }
    ],
    blueprintId: 'takeoff-tools'
  },
  'takeoff-line': {
    title: 'Line Measurement Tool',
    definition: 'Measure linear dimensions like wall lengths, trim runs, or countertop edges.',
    howToUse: [
      '1. Click start point',
      '2. Click end point',
      '3. Length is calculated based on scale',
      '4. Add label for clarity'
    ],
    relatedTo: [
      { feature: 'Linear Items', why: 'Creates linear quantities' },
      { feature: 'Estimates', why: 'Linear feet in estimates' }
    ],
    blueprintId: 'takeoff-tools'
  },
  'takeoff-selection': {
    title: 'Selection Tool',
    definition: 'Mark specific locations where products will be installed (fixtures, outlets, etc.).',
    howToUse: [
      '1. Click to place a marker',
      '2. Link to a material clip',
      '3. Add quantity and notes',
      '4. Shows in estimates'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Selections link to clips' },
      { feature: 'Estimates', why: 'Selections counted in estimates' }
    ],
    blueprintId: 'takeoff-tools'
  },
  'takeoff-filmstrip': {
    title: 'Page Filmstrip',
    definition: 'Navigate between pages of multi-page PDFs and manage page order.',
    howToUse: [
      '1. Click a page to view it',
      '2. Drag pages to reorder',
      '3. Mark pages as "done"',
      '4. Horizontal scroll with mouse wheel'
    ],
    relatedTo: [
      { feature: 'Floor Plans', why: 'Pages of the plan' }
    ],
    blueprintId: 'takeoff-tools'
  },

  // ============================================
  // DRAWING CONTROL
  // ============================================
  'drawing-control': {
    title: 'Drawing Control',
    definition: 'Manage all project drawings including floor plans, elevations, and construction documents.',
    howToUse: [
      'Upload drawing files (PDF, CAD)',
      'Organize by drawing type',
      'Version control for revisions',
      'Share with team and contractors'
    ],
    relatedTo: [
      { feature: 'Floor Plans', why: 'Plans stored here' },
      { feature: 'Takeoff', why: 'Use drawings for takeoff' },
      { feature: 'Documents', why: 'Part of project documents' }
    ],
    blueprintId: 'file-management'
  },

  // ============================================
  // ESTIMATES & PROPOSALS
  // ============================================
  'estimate-page': {
    title: 'Estimate Builder',
    definition: 'Create detailed cost estimates for your design projects. Estimates can include labor, materials, contractors, and markups.',
    howToUse: [
      '1. Add line items manually or import from scope',
      '2. Set quantities and unit costs',
      '3. Apply markups and margins',
      '4. Generate proposal for client',
      '5. Track change orders'
    ],
    relatedTo: [
      { feature: 'Scope Builder', why: 'Items from scope' },
      { feature: 'Clips', why: 'Material pricing' },
      { feature: 'Proposals', why: 'Generate client proposal' },
      { feature: 'Change Orders', why: 'Track revisions' }
    ],
    nextStep: { action: 'Add line items', event: 'addLineItem' },
    blueprintId: 'financials-estimates'
  },
  'estimate-line-item': {
    title: 'Line Item',
    definition: 'Individual cost item in an estimate with description, quantity, unit cost, and category.',
    howToUse: [
      'Enter description and quantity',
      'Set unit cost or link to clip',
      'Choose category for reporting',
      'Toggle visibility to client'
    ],
    relatedTo: [
      { feature: 'Categories', why: 'CSI or custom categories' },
      { feature: 'Clips', why: 'Material pricing from clips' }
    ],
    blueprintId: 'financials-estimates'
  },
  'estimate-markups': {
    title: 'Markups & Fees',
    definition: 'Apply percentage markups for overhead, profit, design fees, or other costs.',
    howToUse: [
      'Set markup percentage',
      'Apply to specific categories',
      'Toggle client visibility',
      'Calculate final total'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Part of estimate total' }
    ],
    blueprintId: 'financials-estimates'
  },
  'change-order': {
    title: 'Change Order',
    definition: 'Document changes to the original scope with cost and schedule impacts.',
    howToUse: [
      '1. Document the change request',
      '2. Calculate cost impact',
      '3. Note schedule impact',
      '4. Get client approval',
      '5. Apply to estimate'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Modifies estimate' },
      { feature: 'Scope', why: 'Changes scope' }
    ],
    blueprintId: 'financials-change-orders'
  },

  // ============================================
  // PROJECTS LIST
  // ============================================
  'projects-list': {
    title: 'All Projects',
    definition: 'View all your active and archived projects organized by phase.',
    howToUse: [
      'Click a project to open Project Hub',
      '"Needs you" shows the decisions waiting and the answers you owe, most overdue first',
      'Use phases to filter by stage',
      'Quick actions: Call, Directions, Details, Clips'
    ],
    relatedTo: [
      { feature: 'Project Hub', why: 'Project workspace' },
      { feature: 'Lead Pipeline', why: 'Leads become projects' }
    ],
    nextStep: { action: 'Create new project', event: 'openCreateProject' },
    blueprintId: 'project-management'
  },

  // ============================================
  // PROJECT HUB ELEMENTS
  // ============================================
  'in-proposal': {
    title: 'Clips In Proposal',
    definition: 'Count of material clips marked as "In Proposal" - ready to be included in estimates.',
    howToUse: [
      'Click to view proposal clips',
      'Add clips to estimates',
      'Track client approvals'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Material selections' },
      { feature: 'Estimates', why: 'Add to estimates' }
    ],
    dataSource: 'Count of clips with status = in_proposal',
    blueprintId: 'clips-library'
  },
  'project-budget': {
    title: 'Project Budget',
    definition: 'Target budget for this project, used to track spending and estimate accuracy.',
    howToUse: [
      'Set during project setup',
      'Compare to estimate totals',
      'Track variance over time'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Compare to estimate' },
      { feature: 'Budget Tracker', why: 'Budget monitoring' }
    ],
    blueprintId: 'financials-budget'
  },
  'project-team': {
    title: 'Team Members',
    definition: 'Count of team members assigned to this project.',
    howToUse: [
      'Click to manage team',
      'Assign roles and permissions',
      'Set what a general contractor sees in his Site Office: his own subs, chosen parties, or everyone',
      'Track workload'
    ],
    relatedTo: [
      { feature: 'Team', why: 'Team management' },
      { feature: 'Tasks', why: 'Task assignments' }
    ],
    blueprintId: 'team-management'
  },

  // ============================================
  // SCOPE OF WORK
  // ============================================
  'scope-builder': {
    title: 'Scope Builder',
    definition: 'Define the complete scope of work by room, including all items to be installed or renovated.',
    howToUse: [
      '1. Select room from takeoff',
      '2. Add scope items (cabinets, counters, etc.)',
      '3. Assign materials from clips',
      '4. Generate scope document'
    ],
    relatedTo: [
      { feature: 'Takeoff', why: 'Rooms from takeoff' },
      { feature: 'Clips', why: 'Materials for scope items' },
      { feature: 'Estimates', why: 'Scope becomes estimate' },
      { feature: 'Contracts', why: 'Scope in contracts' }
    ],
    nextStep: { action: 'Build scope from rooms', event: 'openScopeBuilder' },
    blueprintId: 'scope-management'
  },
  'scope-item': {
    title: 'Scope Item',
    definition: 'Individual item in the scope of work with quantity, material, and pricing.',
    howToUse: [
      'Edit quantity and specifications',
      'Assign material from clips',
      'Set pricing for estimate'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Material from clips' },
      { feature: 'Estimates', why: 'Item in estimate' }
    ],
    blueprintId: 'scope-management'
  },

  // ============================================
  // BIDDING & RFPs
  // ============================================
  'bid-request': {
    title: 'Bid Request (RFP)',
    definition: 'Request for proposal sent to contractors asking for pricing on scope items.',
    howToUse: [
      '1. Select scope items to include',
      '2. Choose contractors to send to',
      '3. Set response deadline',
      '4. Send RFP via email'
    ],
    relatedTo: [
      { feature: 'Scope', why: 'Items being bid' },
      { feature: 'Contractors', why: 'Bid recipients' },
      { feature: 'Emails', why: 'RFP sent via email' }
    ],
    nextStep: { action: 'Create bid request', route: '/bids/new' },
    blueprintId: 'financials-bids'
  },
  'bid-comparison': {
    title: 'Bid Comparison',
    definition: 'Side-by-side comparison of received bids to evaluate contractor pricing.',
    howToUse: [
      'View all received bids',
      'Compare line-by-line pricing',
      'Award winning bid'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Bid submitters' },
      { feature: 'Purchase Orders', why: 'Award creates PO' }
    ],
    blueprintId: 'financials-bids'
  },

  // ============================================
  // CHROME EXTENSION / CLIPPER
  // ============================================
  'clipper-extension': {
    title: 'Clipper Extension',
    definition: 'Chrome browser extension for saving product information from vendor websites directly to your Clip Library.',
    howToUse: [
      '1. Install from Chrome Web Store',
      '2. Visit vendor product page',
      '3. Click KEY-MADE button',
      '4. Product info auto-extracted',
      '5. Save to your library'
    ],
    relatedTo: [
      { feature: 'Clip Library', why: 'Clips saved here' },
      { feature: 'Estimates', why: 'Use clips in estimates' }
    ],
    nextStep: { action: 'Install extension', event: 'installExtension' },
    blueprintId: 'clips-library'
  },

  // ============================================
  // DESIGN COMPANION
  // ============================================
  'design-companion': {
    title: 'Design Companion',
    definition: 'AI assistant for generating room layouts, suggesting materials, and creating design concepts.',
    howToUse: [
      'Describe your design vision',
      'Get AI suggestions',
      'Generate room layouts',
      'Find matching materials'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Suggests materials' },
      { feature: 'Scope', why: 'Helps define scope' }
    ],
    blueprintId: 'ai-tools'
  },

  // ============================================
  // PANEL BUILDER
  // ============================================
  'panel-builder': {
    title: 'Panel Builder',
    definition: 'Create material presentation boards and selection panels for client review.',
    howToUse: [
      'Add clips to panel',
      'Arrange materials visually',
      'Add annotations',
      'Export for presentation'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Materials from clips' },
      { feature: 'Client Portal', why: 'Share with clients' }
    ],
    blueprintId: 'presentation-tools'
  },

  // ============================================
  // CLIENT PORTAL
  // ============================================
  'client-portal': {
    title: 'Client Portal',
    definition: 'External-facing view for clients to review designs, approve selections, and track progress.',
    howToUse: [
      'Share link with clients',
      'Clients view selections',
      'Collect approvals',
      'Track client feedback'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Client approves estimates' },
      { feature: 'Selections', why: 'Client reviews selections' }
    ],
    blueprintId: 'client-communication'
  },
  'client-survey': {
    title: 'Client Survey',
    definition: 'Questionnaire sent to clients to gather project requirements and preferences.',
    howToUse: [
      'Send survey link to client',
      'Client completes online',
      'Responses populate project'
    ],
    relatedTo: [
      { feature: 'Project Info', why: 'Survey populates info' },
      { feature: 'Journey', why: 'Part of onboarding' }
    ],
    blueprintId: 'client-communication'
  },

  // ============================================
  // COMPASS SYSTEM
  // ============================================
  'compass-drawer': {
    title: 'Compass',
    definition: 'Your unified navigation and guidance system. Actions, Journey progress, Learning resources, and System Map all in one place.',
    howToUse: [
      'Click to open guidance panel',
      'Actions tab: Quick context actions',
      'Journey tab: Project workflow steps',
      'Learn tab: Help content for current page',
      'Map tab: System overview'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Shows journey progress' },
      { feature: 'Learning Center', why: 'Links to guides' }
    ],
    blueprintId: 'navigation'
  },
  'help-toggle': {
    title: 'Help Icons Toggle',
    definition: 'Global switch to show or hide the contextual help icons (?) throughout the app.',
    howToUse: [
      'Click eye icon to toggle',
      'When ON: Help icons visible everywhere',
      'When OFF: Clean interface'
    ],
    relatedTo: [
      { feature: 'Compass', why: 'Toggle in Compass header' }
    ],
    blueprintId: 'navigation'
  },

  // ============================================
  // LEAD PIPELINE STAGES
  // ============================================
  'pipeline-inquiry': {
    title: 'Inquiry Stage',
    definition: 'Initial contact from a prospective client. First point of interest.',
    howToUse: [
      'Review incoming inquiry',
      'Capture basic info',
      'Schedule consultation',
      'Move to next stage'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'First pipeline stage' },
      { feature: 'Calendly', why: 'Auto-creates from booking' }
    ],
    blueprintId: 'crm-leads'
  },
  'pipeline-consultation': {
    title: 'Consultation Stage',
    definition: 'Meeting with prospect to understand their project needs and qualify fit.',
    howToUse: [
      'Conduct site visit',
      'Gather requirements',
      'Assess project scope',
      'Prepare for proposal'
    ],
    relatedTo: [
      { feature: 'Calendar', why: 'Schedule consultation' },
      { feature: 'Notes', why: 'Meeting notes' }
    ],
    blueprintId: 'crm-leads'
  },
  'pipeline-proposal': {
    title: 'Proposal Stage',
    definition: 'Creating and presenting your design proposal and pricing to the client.',
    howToUse: [
      'Create estimate/proposal',
      'Present to client',
      'Handle questions',
      'Negotiate terms'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Proposal pricing' },
      { feature: 'Documents', why: 'Proposal documents' }
    ],
    blueprintId: 'crm-leads'
  },
  'pipeline-closing': {
    title: 'Closing Stage',
    definition: 'Final negotiations, contract signing, and securing the project.',
    howToUse: [
      'Finalize contract terms',
      'Collect signatures',
      'Process retainer',
      'Convert to project'
    ],
    relatedTo: [
      { feature: 'Contracts', why: 'Sign contracts' },
      { feature: 'Projects', why: 'Won leads become projects' }
    ],
    blueprintId: 'crm-leads'
  },
  'pipeline-won': {
    title: 'Won Stage',
    definition: 'Successfully closed leads that have been converted to active projects.',
    howToUse: [
      'Click Convert to Project',
      'All lead data transfers',
      'Project created automatically'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Creates new project' },
      { feature: 'Contacts', why: 'Lead becomes client' }
    ],
    blueprintId: 'crm-leads'
  },
  'pipeline-lost': {
    title: 'Lost Stage',
    definition: 'Leads that did not convert. Track for future follow-up or analysis.',
    howToUse: [
      'Record loss reason',
      'Send thank-you email',
      'Archive for reference'
    ],
    relatedTo: [
      { feature: 'Emails', why: 'Send follow-up' }
    ],
    blueprintId: 'crm-leads'
  },

  // ============================================
  // LEAD CARD ELEMENTS
  // ============================================
  'lead-card': {
    title: 'Lead Card',
    definition: 'Summary of a prospective client showing contact info, project type, and pipeline stage.',
    howToUse: [
      'Click to open lead details',
      'Drag to change stage',
      'Use menu for quick actions'
    ],
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Part of pipeline' },
      { feature: 'Contacts', why: 'Contains contact info' }
    ],
    blueprintId: 'crm-leads'
  },
  'lead-details': {
    title: 'Lead Details',
    definition: 'Full information about a lead including contact info, project details, timeline, and communication history.',
    howToUse: [
      'View/edit all lead info',
      'Add notes and tasks',
      'Send emails',
      'Track interactions'
    ],
    relatedTo: [
      { feature: 'Tasks', why: 'Lead-related tasks' },
      { feature: 'Emails', why: 'Communication history' }
    ],
    blueprintId: 'crm-leads'
  },

  // ============================================
  // STATS & METRICS
  // ============================================
  'stat-total-leads': {
    title: 'Total Leads',
    definition: 'Count of all leads in your pipeline including active, won, and lost.',
    dataSource: 'All leads in database',
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'All pipeline leads' }
    ],
    blueprintId: 'crm-leads'
  },
  'stat-active-leads': {
    title: 'Active Leads',
    definition: 'Leads currently in progress (not won or lost).',
    dataSource: 'Leads with stage not in (won, lost)',
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Active pipeline stages' }
    ],
    blueprintId: 'crm-leads'
  },
  'stat-conversion-rate': {
    title: 'Conversion Rate',
    definition: 'Percentage of leads that become won projects.',
    dataSource: 'Won leads / Total closed leads × 100',
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Measures pipeline success' }
    ],
    blueprintId: 'crm-leads'
  },
  'stat-pipeline-value': {
    title: 'Pipeline Value',
    definition: 'Total estimated value of all active leads in the pipeline.',
    dataSource: 'Sum of budget estimates for active leads',
    relatedTo: [
      { feature: 'Lead Pipeline', why: 'Financial pipeline health' }
    ],
    blueprintId: 'crm-leads'
  },

  // ============================================
  // FINANCIALS PAGE
  // ============================================
  'financials-page': {
    title: 'Financials Dashboard',
    definition: 'Centralized view of all financial metrics including revenue, expenses, profit, and outstanding balances across all projects.',
    howToUse: [
      'View key financial KPIs at a glance',
      'Filter by date range or project',
      'Click any metric card for detailed breakdown',
      'Export reports for accounting'
    ],
    relatedTo: [
      { feature: 'Invoices', route: '/financials/invoices', why: 'Revenue tracking' },
      { feature: 'Expenses', route: '/financials/expenses', why: 'Cost tracking' },
      { feature: 'Budget', route: '/financials/budgets', why: 'Budget vs actuals' }
    ],
    nextStep: { action: 'View detailed reports', route: '/financials/reports' },
    blueprintId: 'financials-reports'
  },
  'financials-revenue': {
    title: 'Total Revenue',
    definition: 'Sum of all invoiced amounts from clients across all projects.',
    howToUse: [
      'Click to see invoice breakdown',
      'Compare to previous periods',
      'Track collection status'
    ],
    relatedTo: [
      { feature: 'Invoices', why: 'Revenue from invoices' },
      { feature: 'Projects', why: 'Revenue by project' }
    ],
    dataSource: 'Aggregated from all invoices',
    blueprintId: 'financials-invoices'
  },
  'financials-expenses': {
    title: 'Total Expenses',
    definition: 'Sum of all project costs including materials, labor, subcontractors, and overhead.',
    howToUse: [
      'View by expense category',
      'Track against budget',
      'Identify cost overruns'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Committed costs' },
      { feature: 'Budget', why: 'Budget tracking' }
    ],
    dataSource: 'Sum of all logged expenses',
    blueprintId: 'financials-expenses'
  },
  'financials-profit': {
    title: 'Net Profit',
    definition: 'Revenue minus expenses, showing your actual profit across all projects.',
    howToUse: [
      'Monitor monthly trends',
      'Compare profit by project',
      'Set profit margin targets'
    ],
    relatedTo: [
      { feature: 'Revenue', why: 'Income side' },
      { feature: 'Expenses', why: 'Cost side' }
    ],
    dataSource: 'Revenue - Expenses calculation',
    blueprintId: 'financials-reports'
  },
  'financials-outstanding': {
    title: 'Outstanding Balance',
    definition: 'Total amount owed by clients from unpaid invoices.',
    howToUse: [
      'View aging report',
      'Send payment reminders',
      'Track collection progress'
    ],
    relatedTo: [
      { feature: 'Invoices', why: 'Unpaid invoices' },
      { feature: 'Clients', why: 'Client balances' }
    ],
    dataSource: 'Sum of unpaid invoice amounts',
    blueprintId: 'financials-invoices'
  },

  // ============================================
  // PAINT LIBRARY
  // ============================================
  'paint-library': {
    title: 'Paint Library',
    definition: 'Create and manage standardized paint specification sheets with color selections, finishes, and manufacturer details.',
    howToUse: [
      'Create paint spec templates',
      'Define color palettes by room',
      'Specify finishes and sheens',
      'Generate spec sheets for contractors'
    ],
    relatedTo: [
      { feature: 'Scope Builder', why: 'Paint scope items' },
      { feature: 'Documents', why: 'Generated spec sheets' }
    ],
    nextStep: { action: 'Create paint spec', event: 'openSpecEditor' },
    blueprintId: 'design-tools'
  },

  // ============================================
  // INSURANCE AUDIT
  // ============================================
  'insurance-audit': {
    title: 'Insurance Audit Reports',
    definition: 'Generate contractor payment reports for annual insurance audits. Track all payments to contractors and subcontractors by year.',
    howToUse: [
      'Select reporting year',
      'View company-wide or project-specific',
      'Export to CSV for insurance',
      'Print detailed reports'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Contractor payments' },
      { feature: 'Purchase Orders', why: 'Payment records' }
    ],
    blueprintId: 'financials-reports'
  },

  // ============================================
  // DRAW REQUESTS
  // ============================================
  'draw-requests': {
    title: 'Draw Requests',
    definition: 'Manage construction draw requests for progress billing. Create, submit, and track payment requests based on project completion.',
    howToUse: [
      '1. Create draw request',
      '2. Add line items from budget',
      '3. Calculate completion percentage',
      '4. Submit to title company',
      '5. Track approval status'
    ],
    relatedTo: [
      { feature: 'Budget', why: 'Draw from budget items' },
      { feature: 'Invoices', why: 'Billing cycle' }
    ],
    nextStep: { action: 'Create draw request', event: 'openDrawDialog' },
    blueprintId: 'financials-draws'
  },

  // ============================================
  // WORK TABLE
  // ============================================
  'work-table': {
    title: 'Work Table',
    definition: 'Collaborative spreadsheet-like workspace for project planning and tracking. Supports real-time collaboration with multiple users.',
    howToUse: [
      'Create tables and columns',
      'Add rows for tracking items',
      'Share with team members',
      'Use formulas for calculations',
      'Export to Excel/CSV'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Project tracking' },
      { feature: 'Team', why: 'Collaboration' }
    ],
    blueprintId: 'productivity-tools'
  },

  // ============================================
  // LEARNING CENTER
  // ============================================
  'learning-center': {
    title: 'Learning Center',
    definition: 'Your guide to KEY-MADE. Browse help articles, tutorials, and system documentation organized by topic.',
    howToUse: [
      'Search for specific topics',
      'Browse by category',
      'Click articles to learn more',
      'Use quick links to features'
    ],
    relatedTo: [
      { feature: 'Compass', why: 'Opens from Compass drawer' },
      { feature: 'Help Icons', why: 'Detailed explanations' }
    ],
    blueprintId: 'navigation'
  },

  // ============================================
  // DOCUMENT TOOLS
  // ============================================
  'document-generator': {
    title: 'Document Generator',
    definition: 'Generate project documents from templates. Create proposals, contracts, letters of agreement, and more.',
    howToUse: [
      'Select document type',
      'Choose template',
      'Customize with project data',
      'Preview and generate',
      'Send to client or download'
    ],
    relatedTo: [
      { feature: 'Templates', why: 'Uses document templates' },
      { feature: 'Projects', why: 'Pulls project data' },
      { feature: 'Clients', why: 'Send to clients' }
    ],
    blueprintId: 'documentation'
  },
  'document-editor': {
    title: 'Document Editor',
    definition: 'Create and edit document templates with merge fields that auto-populate from project data.',
    howToUse: [
      'Use rich text editor',
      'Insert merge fields {{ }}',
      'Preview with sample data',
      'Save as template'
    ],
    relatedTo: [
      { feature: 'Templates', why: 'Saves as template' },
      { feature: 'Documents', why: 'Used by document generator' }
    ],
    blueprintId: 'documentation'
  },
  'project-detail': {
    title: 'Project Detail',
    definition: 'Overview of a project showing clips, materials, and quick actions.',
    howToUse: [
      'View project clips',
      'See material selections',
      'Navigate to Project Hub'
    ],
    relatedTo: [
      { feature: 'Project Hub', why: 'Full project workspace' },
      { feature: 'Clips', why: 'Material library' }
    ],
    blueprintId: 'project-management'
  },
  'project-gallery': {
    title: 'Project Gallery',
    definition: 'Upload, organize, and manage project photos. Control visibility across portals and dashboards.',
    howToUse: [
      'Upload photos via drag & drop',
      'Organize with categories',
      'Set visibility per portal',
      'Mark hero images with star'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Project photos' },
      { feature: 'Client Portal', why: 'Shared with clients' }
    ],
    blueprintId: 'file-management'
  },
  'contractor-portal': {
    title: 'Contractor Portal',
    definition: 'External portal for contractors to view project details, submit bids, update task status, and communicate.',
    howToUse: [
      'Contractor logs in with email',
      'View assigned projects',
      'Submit bid responses',
      'Update task progress',
      'Upload documents'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Contractor access' },
      { feature: 'Bids', why: 'Bid submission' },
      { feature: 'Tasks', why: 'Task updates' }
    ],
    blueprintId: 'contractor-management'
  },

  // ============================================
  // PROCUREMENT DASHBOARD
  // ============================================
  'procurement-dashboard': {
    title: 'Procurement Dashboard',
    definition: 'Central hub for all purchasing activities including purchase orders, vendor management, and delivery tracking.',
    howToUse: [
      'View all open orders',
      'Track delivery status',
      'Manage vendor relationships',
      'Create new purchase orders'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', route: '/purchase-orders', why: 'All POs' },
      { feature: 'Vendors', route: '/vendors', why: 'Vendor directory' },
      { feature: 'Receiving', why: 'Track deliveries' }
    ],
    nextStep: { action: 'Create purchase order', route: '/purchase-orders?action=new' },
    blueprintId: 'financials-procurement'
  },
  'procurement-open-orders': {
    title: 'Open Orders',
    definition: 'Purchase orders that have been sent but not yet fully received.',
    howToUse: [
      'Click to view order details',
      'Track expected delivery dates',
      'Follow up with vendors'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Order list' },
      { feature: 'Receiving', why: 'Receive items' }
    ],
    dataSource: 'Count of POs with status = pending, confirmed, shipped',
    blueprintId: 'financials-procurement'
  },
  'procurement-total-value': {
    title: 'Total Order Value',
    definition: 'Combined value of all purchase orders in the current period.',
    howToUse: [
      'Monitor spending trends',
      'Compare to budget',
      'Forecast cash flow'
    ],
    relatedTo: [
      { feature: 'Budget', why: 'Spending against budget' },
      { feature: 'Expenses', why: 'Committed costs' }
    ],
    dataSource: 'Sum of all PO amounts',
    blueprintId: 'financials-procurement'
  },
  'procurement-pending-delivery': {
    title: 'Pending Deliveries',
    definition: 'Items ordered but not yet received at the project site.',
    howToUse: [
      'Track expected arrival dates',
      'Coordinate with project schedule',
      'Contact vendors for updates'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Ordered items' },
      { feature: 'Schedule', why: 'Installation timing' }
    ],
    dataSource: 'Items from POs with received_qty < ordered_qty',
    blueprintId: 'financials-procurement'
  },

  // ============================================
  // VENDORS PAGE
  // ============================================
  'vendors-page': {
    title: 'Vendors Directory',
    definition: 'Your directory of suppliers, showrooms, and trade partners with contact info and ordering details.',
    howToUse: [
      'Search by name or category',
      'View vendor details and contacts',
      'Create purchase orders',
      'Track preferred pricing'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', route: '/purchase-orders', why: 'Order from vendors' },
      { feature: 'Clips', why: 'Materials from vendors' },
      { feature: 'Contacts', why: 'Vendor contacts' }
    ],
    nextStep: { action: 'Add new vendor', event: 'openVendorDialog' },
    blueprintId: 'vendor-management'
  },
  'vendor-card': {
    title: 'Vendor Card',
    definition: 'Summary of a vendor showing company info, contact details, and account status.',
    howToUse: [
      'Click to view full details',
      'Star to mark as favorite',
      'Quick actions: call, email, create PO'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'Order from this vendor' },
      { feature: 'Pricing', why: 'Vendor-specific pricing' }
    ],
    blueprintId: 'vendor-management'
  },
  'vendor-favorites': {
    title: 'Favorite Vendors',
    definition: 'Vendors you use frequently, marked with a star for quick access.',
    howToUse: [
      'Click star to add/remove favorites',
      'Filter to show only favorites',
      'Favorites appear at top of lists'
    ],
    relatedTo: [
      { feature: 'Vendors', why: 'All vendors' }
    ],
    blueprintId: 'vendor-management'
  },

  // ============================================
  // PURCHASE ORDERS PAGE
  // ============================================
  'purchase-orders-page': {
    title: 'Purchase Orders',
    definition: 'Manage all purchase orders from creation through receiving and payment.',
    howToUse: [
      'Create new POs from bids or manually',
      'Track order status',
      'Receive items when delivered',
      'Match to vendor invoices'
    ],
    relatedTo: [
      { feature: 'Vendors', route: '/vendors', why: 'Order from vendors' },
      { feature: 'Bids', why: 'POs from awarded bids' },
      { feature: 'Receiving', why: 'Track deliveries' }
    ],
    nextStep: { action: 'Create new PO', event: 'openPODialog' },
    blueprintId: 'financials-procurement'
  },
  'po-status-draft': {
    title: 'Draft POs',
    definition: 'Purchase orders being prepared but not yet sent to vendors.',
    howToUse: [
      'Complete order details',
      'Review and finalize',
      'Send when ready'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'All POs' }
    ],
    blueprintId: 'financials-procurement'
  },
  'po-status-ordered': {
    title: 'Ordered POs',
    definition: 'Purchase orders sent to vendors awaiting delivery.',
    howToUse: [
      'Track expected delivery',
      'Contact vendor for updates',
      'Receive items when arrived'
    ],
    relatedTo: [
      { feature: 'Receiving', why: 'Receive items' }
    ],
    blueprintId: 'financials-procurement'
  },
  'po-status-received': {
    title: 'Received POs',
    definition: 'Purchase orders where all items have been received.',
    howToUse: [
      'Verify quantities',
      'Match to vendor invoice',
      'Mark as complete'
    ],
    relatedTo: [
      { feature: 'Expenses', why: 'Cost recognized' }
    ],
    blueprintId: 'financials-procurement'
  },

  // ============================================
  // INVOICES PAGE
  // ============================================
  'invoices-page': {
    title: 'Invoices',
    definition: 'Create, send, and track client invoices for your design services.',
    howToUse: [
      'Create invoices from estimates',
      'Send to clients via email',
      'Track payment status',
      'Record payments received'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Invoices from estimates' },
      { feature: 'Payments', why: 'Payment tracking' },
      { feature: 'Financials', why: 'Revenue tracking' }
    ],
    nextStep: { action: 'Create invoice', route: '/financials/invoices/new' },
    blueprintId: 'financials-invoices'
  },
  'invoice-outstanding': {
    title: 'Outstanding Invoices',
    definition: 'Invoices that have been sent but not yet fully paid.',
    howToUse: [
      'Sort by days overdue',
      'Send payment reminders',
      'View aging report'
    ],
    relatedTo: [
      { feature: 'Payments', why: 'Record payments' }
    ],
    dataSource: 'Invoices with balance > 0',
    blueprintId: 'financials-invoices'
  },

  // ============================================
  // RECEIVED INVOICES PAGE
  // ============================================
  'received-invoices-page': {
    title: 'Received Invoices',
    definition: 'Track and manage invoices received from contractors, subcontractors, and vendors for payment processing and draw requests.',
    howToUse: [
      'Upload or enter invoices from vendors',
      'Match invoices to purchase orders',
      'Review and approve for payment',
      'Include in draw requests to clients',
      'Apply markups and track payment status'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', route: '/purchase-orders', why: 'Match invoices to POs' },
      { feature: 'Draw Requests', why: 'Include in draw requests' },
      { feature: 'Expenses', why: 'Track project costs' }
    ],
    nextStep: { action: 'Add a received invoice', event: 'openCreateDialog' },
    blueprintId: 'financials-procurement'
  },

  // ============================================
  // RFI & SUBMITTALS PAGE
  // ============================================
  'rfi-submittals-page': {
    title: 'RFIs & Submittals',
    definition: 'Manage Requests for Information (RFIs) and product submittals throughout the construction administration phase.',
    howToUse: [
      'Create and track RFIs for clarification requests',
      'Log submittals for product approvals',
      'Track ball-in-court status (who needs to respond)',
      'Record responses and decisions',
      'Issue your own shop drawing for the party to confirm; accept it when they do',
      'Send a question to a vendor; they answer at their own address, no login',
      'A general contractor can answer his own sub in the thread; the final answer stays yours',
      'What you owe shows on All Projects under "Needs you" — answer, review, accept or release from there',
      'Maintain audit trail of communications'
    ],
    relatedTo: [
      { feature: 'Documents', why: 'Attach supporting documents' },
      { feature: 'Team', why: 'Assign to team members' },
      { feature: 'Contractors', why: 'Communicate with contractors' }
    ],
    nextStep: { action: 'Create an RFI', event: 'openCreateRFI' },
    blueprintId: 'construction-admin'
  },

  // ============================================
  // BUDGET TRACKER
  // ============================================
  // FINANCIAL ROADMAP
  // ============================================
  'financial-roadmap': {
    title: 'Financial Roadmap',
    definition: 'Visual workflow guide showing the progression of financial activities for a project - from budgeting through payment collection.',
    howToUse: [
      'Follow the 7-step financial workflow',
      'Green checkmarks indicate completed steps',
      'Amber indicates work in progress',
      'Click any step to navigate directly',
      'Track totals at each stage'
    ],
    relatedTo: [
      { feature: 'Budget', route: '/financials/budgets', why: 'Step 1 - Set project budget' },
      { feature: 'Estimates', why: 'Step 2 - Client proposals' },
      { feature: 'Purchase Orders', why: 'Step 3 - Order materials' },
      { feature: 'Invoices', why: 'Steps 4-5 - Track costs and billing' },
      { feature: 'Draw Requests', why: 'Step 6 - Request loan funds' }
    ],
    nextStep: { action: 'Complete the next step', event: 'navigateToStep' },
    blueprintId: 'financials-overview'
  },

  // ============================================
  'budget-tracker': {
    title: 'Budget Tracker',
    definition: 'Compare estimated costs vs actual spending across all budget categories.',
    howToUse: [
      'View budget vs actuals',
      'Track variance by category',
      'Identify cost overruns early',
      'Update budget allocations'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Budget from estimates' },
      { feature: 'Expenses', why: 'Actual costs' },
      { feature: 'Purchase Orders', why: 'Committed costs' }
    ],
    blueprintId: 'financials-budget'
  },
  'budget-variance': {
    title: 'Budget Variance',
    definition: 'Difference between budgeted amount and actual/committed spending.',
    howToUse: [
      'Green = under budget',
      'Red = over budget',
      'Click for category details'
    ],
    relatedTo: [
      { feature: 'Budget', why: 'Budget amounts' },
      { feature: 'Expenses', why: 'Actual costs' }
    ],
    dataSource: 'Budget amount - (Expenses + Committed)',
    blueprintId: 'financials-budget'
  },

  // ============================================
  // CONTRACTORS PAGE
  // ============================================
  'contractors-page': {
    title: 'Contractors Directory',
    definition: 'Your list of trusted contractors, subcontractors, and tradespeople.',
    howToUse: [
      'Browse by trade category',
      'View past project history',
      'Send bid requests',
      'Rate contractor performance'
    ],
    relatedTo: [
      { feature: 'Bids', route: '/bids', why: 'Send bid requests' },
      { feature: 'Projects', why: 'Assign to projects' },
      { feature: 'Contacts', why: 'Contractor contacts' }
    ],
    nextStep: { action: 'Add contractor', event: 'openContractorDialog' },
    blueprintId: 'contractor-management'
  },
  'contractor-card': {
    title: 'Contractor',
    definition: 'A contractor or subcontractor in your network with their trade info and history.',
    howToUse: [
      'Click to view full profile',
      'See past projects',
      'Request bid on new work'
    ],
    relatedTo: [
      { feature: 'Bids', why: 'Bid requests' },
      { feature: 'Projects', why: 'Project assignments' }
    ],
    blueprintId: 'contractor-management'
  },

  // ============================================
  // QUALITY CONTROL / PUNCH LISTS
  // ============================================
  'quality-control-page': {
    title: 'Quality Control & Punch Lists',
    definition: 'Create and manage punch lists for tracking construction defects, incomplete work, and final inspections before project completion.',
    howToUse: [
      'Create punch lists for each inspection phase',
      'Add items with photos and descriptions',
      'Assign items to contractors',
      'Track resolution status',
      'Generate final walk-through reports'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Assign defect resolution' },
      { feature: 'Progress', why: 'Log inspection progress' },
      { feature: 'Documents', why: 'Generate punch list reports' }
    ],
    nextStep: { action: 'Create a punch list', event: 'openNewListDialog' },
    blueprintId: 'construction-admin'
  },

  // ============================================
  // ADMIN & SETTINGS
  // ============================================
  'admin-dashboard': {
    title: 'Admin Dashboard',
    definition: 'Administrative controls and company-wide settings for KEY-MADE.',
    howToUse: [
      'Manage team members',
      'Configure company settings',
      'View system usage',
      'Manage templates'
    ],
    relatedTo: [
      { feature: 'Team', route: '/team', why: 'Team management' },
      { feature: 'Settings', route: '/settings', why: 'App settings' }
    ],
    blueprintId: 'admin-settings'
  },
  'company-settings': {
    title: 'Company Settings',
    definition: 'Configure your company profile, branding, and business preferences.',
    howToUse: [
      'Update company info',
      'Upload logo and branding',
      'Set default preferences',
      'Configure integrations'
    ],
    relatedTo: [
      { feature: 'Admin', why: 'Admin controls' },
      { feature: 'Templates', why: 'Document branding' }
    ],
    blueprintId: 'admin-settings'
  },
  'team-management': {
    title: 'Team Management',
    definition: 'Add, remove, and manage team members and their access permissions.',
    howToUse: [
      'Invite new team members',
      'Set user roles and permissions',
      'View team activity',
      'Manage access levels'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Project assignments' },
      { feature: 'Tasks', why: 'Task assignments' }
    ],
    blueprintId: 'team-management'
  },

  // ============================================
  // DOCUMENT LIBRARY
  // ============================================
  'document-library': {
    title: 'Document Library',
    definition: 'Store and organize all your document templates, contracts, and specifications.',
    howToUse: [
      'Browse templates by category',
      'Create new documents',
      'Edit and customize templates',
      'Share with team'
    ],
    relatedTo: [
      { feature: 'Contracts', why: 'Contract templates' },
      { feature: 'Specifications', why: 'Spec sheets' },
      { feature: 'Projects', why: 'Project documents' }
    ],
    nextStep: { action: 'Create document', route: '/documents/editor/new' },
    blueprintId: 'documentation'
  },

  // ============================================
  // NOTES PAGE
  // ============================================
  'notes-page': {
    title: 'Notes',
    definition: 'Quick notes, memos, and documentation organized by project or general.',
    howToUse: [
      'Create quick notes',
      'Organize by project',
      'Pin important notes',
      'Search past notes'
    ],
    relatedTo: [
      { feature: 'Projects', why: 'Project notes' },
      { feature: 'Quick Memo', why: 'Voice memos' }
    ],
    nextStep: { action: 'Create note', event: 'createNote' },
    blueprintId: 'documentation'
  },

  // ============================================
  // CORNER OF THE DESK (Personal Space)
  // ============================================
  'corner-of-desk': {
    title: 'Corner of the Desk',
    definition: 'Your private personal space - only visible to you. Store personal work tables, track personal budgets, and save private items that are separate from company data.',
    howToUse: [
      'Create personal work tables for private tracking',
      'Set and monitor personal budget goals',
      'Save personal reference items',
      'Keep sensitive notes separate from project data'
    ],
    relatedTo: [
      { feature: 'Work Tables', route: '/work-tables', why: 'Personal version of work tables' },
      { feature: 'My Desk', route: '/', why: 'Your main dashboard' }
    ],
    nextStep: { action: 'Create a personal work table', event: 'createWorkTable' },
    blueprintId: 'personal-workspace'
  },

  // ============================================
  // TERMS GLOSSARY PAGE
  // ============================================
  'terms-glossary': {
    title: 'Terms Glossary',
    definition: 'A comprehensive dictionary of architectural, interior design, and project management terminology with KEY-MADE application tips.',
    howToUse: [
      'Search for any term',
      'Filter by category (Design, Materials, Financial, etc.)',
      'Click any term to see how to use it in KEY-MADE',
      'Learn industry-standard definitions'
    ],
    relatedTo: [
      { feature: 'Blueprint Library', route: '/blueprints', why: 'Learn concepts in detail' },
      { feature: 'Knowledge Base', route: '/knowledge', why: 'Save custom definitions' }
    ],
    nextStep: { action: 'Search a term', event: 'searchTerm' },
    blueprintId: 'learning-resources'
  },

  // ============================================
  // ALL ACTIVITY PAGE
  // ============================================
  'all-activity': {
    title: 'All Activity',
    definition: 'A comprehensive timeline of all project activity across your workspace, including tasks completed, documents added, views, and team actions.',
    howToUse: [
      'Filter by project to focus on one at a time',
      'Search for specific activities',
      'View activity by month',
      'Track team member contributions'
    ],
    relatedTo: [
      { feature: 'Projects', route: '/projects', why: 'Activity grouped by project' },
      { feature: 'Tasks', route: '/tasks', why: 'Shows task completions' },
      { feature: 'Calendar', route: '/calendar', why: 'Activities on timeline' }
    ],
    nextStep: { action: 'Filter by project', event: 'filterProject' },
    blueprintId: 'activity-tracking'
  },

  // ============================================
  // NOTIFICATIONS PAGE
  // ============================================
  'notifications-page': {
    title: 'Notifications',
    definition: 'Centralized view of all system notifications including messages, document signatures, task due dates, and project updates.',
    howToUse: [
      'Click a notification to navigate to the related item',
      'Mark individual or all notifications as read',
      'Filter by unread, read, or all',
      'Delete notifications you no longer need'
    ],
    relatedTo: [
      { feature: 'Tasks', why: 'Task due reminders' },
      { feature: 'Documents', why: 'Signature notifications' },
      { feature: 'Messages', why: 'New message alerts' }
    ],
    nextStep: { action: 'View notification settings', route: '/settings' },
    blueprintId: 'communication'
  },

  // ============================================
  // KNOWLEDGE BASE PAGE
  // ============================================
  'knowledge-base': {
    title: 'Knowledge Base',
    definition: 'Your personal and company library for storing notes, documents, databases, and reference materials organized in folders.',
    howToUse: [
      'Create folders to organize content',
      'Add notes, tables (databases), and links',
      'Import from Notion',
      'Tag items for easy searching',
      'Share company-wide or keep personal'
    ],
    relatedTo: [
      { feature: 'Notes', route: '/notes', why: 'Quick notes go here' },
      { feature: 'Documents', route: '/documents', why: 'Document storage' },
      { feature: 'Blueprint Library', route: '/blueprints', why: 'Learning resources' }
    ],
    nextStep: { action: 'Create a folder', event: 'createFolder' },
    blueprintId: 'documentation'
  },

  // ============================================
  // RESOURCES & HELP PAGE
  // ============================================
  'resources-page': {
    title: 'Resources & Help',
    definition: 'Central hub for learning KEY-MADE including keyboard shortcuts, workflow guides, training videos, and support contact information.',
    howToUse: [
      'Learn keyboard shortcuts for faster workflow',
      'Follow step-by-step workflow guides',
      'Watch training videos',
      'Contact support for assistance'
    ],
    relatedTo: [
      { feature: 'Blueprint Library', route: '/blueprints', why: 'In-depth learning' },
      { feature: 'Compass', why: 'Contextual help' },
      { feature: 'Terms Glossary', route: '/terms-glossary', why: 'Industry terminology' }
    ],
    nextStep: { action: 'Learn a keyboard shortcut', event: 'viewShortcuts' },
    blueprintId: 'learning-resources'
  },

  // ============================================
  // SURVEY TEMPLATES PAGE
  // ============================================
  'survey-templates': {
    title: 'Survey Templates',
    definition: 'Create and manage questionnaire templates for client intake, project feedback, and information gathering.',
    howToUse: [
      'Create custom survey templates',
      'Add various question types (text, multiple choice, rating)',
      'Send surveys to clients via email or link',
      'View and analyze survey responses',
      'Use responses to populate project details'
    ],
    relatedTo: [
      { feature: 'Projects', route: '/projects', why: 'Surveys link to projects' },
      { feature: 'Leads', route: '/pipeline', why: 'Intake surveys for new leads' },
      { feature: 'Client Portal', why: 'Clients complete surveys online' }
    ],
    nextStep: { action: 'Create a survey template', event: 'createTemplate' },
    blueprintId: 'client-communication'
  },

  // ============================================
  // BLUEPRINT LIBRARY PAGE
  // ============================================
  'blueprint-library': {
    title: 'Blueprint Library',
    definition: 'Comprehensive learning resources and system documentation organized by topic. Explore workflows, best practices, and detailed feature guides.',
    howToUse: [
      'Browse by category (Getting Started, Financials, Design Tools)',
      'Search for specific topics',
      'Follow step-by-step workflow guides',
      'Learn system architecture and concepts',
      'Access video tutorials and walkthroughs'
    ],
    relatedTo: [
      { feature: 'Terms Glossary', route: '/terms-glossary', why: 'Industry terminology' },
      { feature: 'Resources', route: '/resources', why: 'Quick reference guides' },
      { feature: 'Compass', why: 'Contextual help links to blueprints' }
    ],
    nextStep: { action: 'Explore a topic', event: 'browseTopic' },
    blueprintId: 'learning-resources'
  },

  // ============================================
  // COMPANY SETTINGS PAGE
  // ============================================
  'company-settings': {
    title: 'Company Settings',
    definition: 'Admin page for configuring company branding, logos, colors, watermarks, portal settings, and email templates that appear across all documents and client portals.',
    howToUse: [
      'Upload your primary and light logos',
      'Set brand colors for documents',
      'Configure the client portal (Drawing Room) welcome message',
      'Set up watermarks for draft documents',
      'Manage email templates'
    ],
    relatedTo: [
      { feature: 'Drawing Room', why: 'Portal branding settings' },
      { feature: 'Documents', why: 'Document headers and footers' },
      { feature: 'Mood Boards', why: 'Watermark settings' }
    ],
    nextStep: { action: 'Upload your logo', event: 'uploadLogo' },
    blueprintId: 'admin-settings'
  },

  // ============================================
  // SCHEDULED EMAILS DASHBOARD
  // ============================================
  'scheduled-emails-dashboard': {
    title: 'Scheduled Emails Dashboard',
    definition: 'View and manage all emails scheduled to be sent across your projects. Scheduled emails are queued from the project wizard and journey automation.',
    howToUse: [
      'View all pending scheduled emails',
      'See when each email will be sent',
      'Cancel emails before they are sent',
      'Filter and search by project or recipient'
    ],
    relatedTo: [
      { feature: 'Projects', route: '/projects', why: 'Emails link to projects' },
      { feature: 'Journey', why: 'Journey automates email scheduling' },
      { feature: 'Notifications', route: '/notifications', why: 'Track sent emails' }
    ],
    nextStep: { action: 'Review pending emails', event: 'reviewEmails' },
    blueprintId: 'client-communication'
  },

  // ============================================
  // PROJECT HUB - SIDEBAR SECTIONS
  // ============================================
  'hub-overview-section': {
    title: 'Overview Section',
    definition: 'Core project information including basic details and site conditions. Start here to understand and configure your project fundamentals.',
    howToUse: ['View project info', 'Configure site conditions', 'Set up project basics'],
    relatedTo: [{ feature: 'Project Info', why: 'Edit core details' }, { feature: 'Site', why: 'Site-specific data' }]
  },
  'hub-info': {
    title: 'Project Info',
    definition: 'Core project details including client information, project address, phase, status, team assignments, and key dates.',
    howToUse: [
      'Edit client name, email, phone',
      'Set project address and location',
      'Assign project phase (SD, DD, CD, etc.)',
      'Update status (Active, On Hold, Completed)',
      'Add or remove team members'
    ],
    relatedTo: [
      { feature: 'Team', why: 'Manage project team here' },
      { feature: 'Journey', why: 'Phase connects to journey' }
    ],
    nextStep: { action: 'Update project details', event: 'editProject' }
  },
  'hub-site': {
    title: 'Site Conditions',
    definition: 'Detailed site information including location, access, utilities, existing conditions, and site-specific requirements for design and construction.',
    howToUse: [
      'Document site access and parking',
      'Note utility locations and capacities',
      'Record existing conditions',
      'Upload site photos',
      'Track permits and approvals'
    ],
    relatedTo: [
      { feature: 'Progress', why: 'Site visits logged here' },
      { feature: 'Photos', why: 'Site documentation' }
    ],
    nextStep: { action: 'Add site conditions', event: 'addSiteConditions' }
  },
  'hub-management-section': {
    title: 'Management Section',
    definition: 'Project workflow and coordination tools including journey tracking, action items, progress logs, team management, and communication.',
    howToUse: ['Track project journey', 'Manage actions', 'Log progress', 'Coordinate team'],
    relatedTo: [{ feature: 'Journey', why: 'Workflow phases' }, { feature: 'Actions', why: 'Task tracking' }]
  },
  'hub-journey': {
    title: 'Project Journey',
    definition: 'Visual workflow tracker showing all project phases from initial consultation through closeout. Track progress, complete milestones, and see what comes next.',
    howToUse: [
      'View current phase and progress',
      'Complete phase steps by checking them off',
      'See upcoming phases and requirements',
      'Jump to specific phases',
      'Track overall project completion percentage'
    ],
    relatedTo: [
      { feature: 'Actions', why: 'Phase tasks become actions' },
      { feature: 'Progress', why: 'Journey drives progress' }
    ],
    nextStep: { action: 'Complete next milestone', event: 'completeStep' }
  },
  'hub-actions': {
    title: 'Action Items',
    definition: 'Task management for the project. Create, assign, and track action items with due dates, priorities, and status updates.',
    howToUse: [
      'Create new action items',
      'Assign to team members',
      'Set due dates and priorities',
      'Mark items complete',
      'Filter by status, assignee, or phase'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Actions from journey phases' },
      { feature: 'Team', why: 'Assigned to team members' }
    ],
    nextStep: { action: 'Create an action', event: 'createAction' }
  },
  'hub-progress': {
    title: 'Progress Log',
    definition: 'Chronological log of site visits, work completed, issues encountered, and project updates with photos and notes.',
    howToUse: [
      'Add daily or weekly progress entries',
      'Upload progress photos',
      'Document issues or delays',
      'Track weather conditions',
      'Note contractor activity'
    ],
    relatedTo: [
      { feature: 'Site', why: 'Site visit documentation' },
      { feature: 'Activity', why: 'Progress is logged as activity' }
    ],
    nextStep: { action: 'Log progress', event: 'logProgress' }
  },
  'hub-team': {
    title: 'Project Team',
    definition: 'Manage all team members, contractors, and consultants assigned to this project with roles, contact info, and access permissions.',
    howToUse: [
      'Add team members and contractors',
      'Assign roles and responsibilities',
      'View contact information',
      'Send team communications',
      'Set portal access permissions'
    ],
    relatedTo: [
      { feature: 'Actions', why: 'Assign tasks to team' },
      { feature: 'Emails', why: 'Team communications' }
    ],
    nextStep: { action: 'Add team member', event: 'addTeamMember' }
  },
  'hub-emails': {
    title: 'Project Emails',
    definition: 'Email communications center for this project. Send, receive, and archive all project-related correspondence with templates and tracking.',
    howToUse: [
      'View all project emails',
      'Compose new emails using templates',
      'Track email delivery status',
      'Archive important correspondence',
      'Search email history'
    ],
    relatedTo: [
      { feature: 'Team', why: 'Email team members' },
      { feature: 'Documents', why: 'Send documents via email' }
    ],
    nextStep: { action: 'Compose email', event: 'composeEmail' }
  },
  'hub-decisions': {
    title: 'Decision Log',
    definition: 'Track and document all project decisions with full audit trail. Record who made decisions, why they were made, and what alternatives were considered.',
    howToUse: [
      'Click to open Decision Log',
      'Add new decisions with context',
      'Track cost and schedule impacts',
      'Link decisions to RFIs, change orders',
      'Review decision history and rationale'
    ],
    relatedTo: [
      { feature: 'Change Orders', why: 'Decisions lead to changes' },
      { feature: 'RFIs', why: 'RFIs require decisions' },
      { feature: 'Meetings', why: 'Decisions made in meetings' }
    ],
    nextStep: { action: 'Log a decision', event: 'logDecision' }
  },
  'decision-log-page': {
    title: 'Decision Log',
    definition: 'Comprehensive decision tracking system with audit trail. Document project decisions, rationale, stakeholders, and impacts for accountability and future reference.',
    howToUse: [
      'Click "Log Decision" to add new',
      'Categorize by type (design, budget, scope)',
      'Record stakeholders and decision makers',
      'Document rationale and alternatives',
      'Track cost and schedule impacts'
    ],
    relatedTo: [
      { feature: 'Change Orders', why: 'Decisions trigger changes' },
      { feature: 'RFIs', why: 'Decisions answer RFIs' },
      { feature: 'Meeting Notes', why: 'Record meeting decisions' }
    ],
    nextStep: { action: 'Log your first decision', event: 'addDecision' }
  },
  'hub-tools-section': {
    title: 'Tools Section',
    definition: 'AI-powered design tools, electrical planning, and portfolio generation for professional project documentation.',
    howToUse: ['Use AI design assistant', 'Plan electrical panels', 'Generate project portfolios'],
    relatedTo: [{ feature: 'Design AI', why: 'AI assistance' }, { feature: 'Portfolio', why: 'Project showcase' }]
  },
  'hub-design-ai': {
    title: 'Design Console (AI)',
    definition: 'AI-powered design assistant that helps with space planning, color selection, material recommendations, and design decisions.',
    howToUse: [
      'Ask design questions',
      'Get material suggestions',
      'Analyze space requirements',
      'Generate design concepts',
      'Review AI recommendations'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'AI suggests from clips' },
      { feature: 'Moods', why: 'Design direction' }
    ],
    nextStep: { action: 'Ask design question', event: 'askDesignAI' }
  },
  'hub-panel-builder': {
    title: 'Electrical Panel Builder',
    definition: 'Tool for designing electrical panel schedules including circuit layout, load calculations, and panel specifications.',
    howToUse: [
      'Create panel schedule',
      'Add circuits and loads',
      'Calculate total load',
      'Generate panel diagram',
      'Export for contractor'
    ],
    relatedTo: [
      { feature: 'Scope Builder', why: 'Electrical scope items' },
      { feature: 'Estimates', why: 'Electrical costs' }
    ],
    nextStep: { action: 'Create panel schedule', event: 'createPanel' }
  },
  'hub-portfolio': {
    title: 'Portfolio Generator',
    definition: 'Create professional project portfolio documents and presentations showcasing completed work with photos, descriptions, and project details.',
    howToUse: [
      'Select project images',
      'Add project description',
      'Choose layout template',
      'Generate PDF or share link',
      'Include before/after comparisons'
    ],
    relatedTo: [
      { feature: 'Photos', why: 'Project images' },
      { feature: 'Documents', why: 'Export portfolio' }
    ],
    nextStep: { action: 'Generate portfolio', event: 'generatePortfolio' }
  },
  'hub-design-section': {
    title: 'Design Section',
    definition: 'Design tools for material selection, mood boards, paint specifications, floor plans, drawings, and scope of work.',
    howToUse: ['Manage clips', 'Create mood boards', 'Specify paint', 'Work with plans'],
    relatedTo: [{ feature: 'Clips', why: 'Material library' }, { feature: 'Moods', why: 'Design direction' }]
  },
  'hub-clips': {
    title: 'Project Clips',
    definition: 'Collection of products, materials, and finishes selected for this project. Clips are captured from websites or added manually and organized by room or category.',
    howToUse: [
      'View all clipped items',
      'Organize by room or category',
      'Add items to estimates',
      'Create mood boards from clips',
      'Share selections with clients'
    ],
    relatedTo: [
      { feature: 'Moods', why: 'Add clips to mood boards' },
      { feature: 'Estimates', why: 'Clips have pricing' }
    ],
    nextStep: { action: 'Add a clip', event: 'addClip' }
  },
  'hub-moods': {
    title: 'Mood Boards',
    definition: 'Visual design boards combining clips, images, and inspiration to communicate design direction and material palettes to clients.',
    howToUse: [
      'Create new mood boards',
      'Drag clips onto boards',
      'Add text and labels',
      'Export as PDF',
      'Share with clients via portal'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Source materials' },
      { feature: 'Portals', why: 'Share with clients' }
    ],
    nextStep: { action: 'Create mood board', event: 'createMoodBoard' }
  },
  'hub-paint': {
    title: 'Paint Specifications',
    definition: 'Paint color selection and specification tool. Build paint schedules by room with brand, color, finish, and location details.',
    howToUse: [
      'Select paint colors by brand',
      'Assign colors to rooms',
      'Specify finish types',
      'Calculate quantities',
      'Generate paint schedule document'
    ],
    relatedTo: [
      { feature: 'Scope Builder', why: 'Paint specifications' },
      { feature: 'Estimates', why: 'Paint costs' }
    ],
    nextStep: { action: 'Add paint color', event: 'addPaintColor' }
  },
  'hub-plans-takeoff': {
    title: 'Plans (Takeoff)',
    definition: 'Floor plan viewer and measurement tool. Upload plans, take measurements, calculate areas, and annotate drawings for estimating and coordination.',
    howToUse: [
      'Upload floor plans (PDF, images)',
      'Set drawing scale',
      'Measure distances and areas',
      'Add annotations and markups',
      'Export measurements for estimates'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Quantities from takeoffs' },
      { feature: 'Drawing Control', why: 'Manage plan versions' }
    ],
    nextStep: { action: 'Upload a plan', event: 'uploadPlan' }
  },
  'hub-drawing-control': {
    title: 'Drawing Control',
    definition: 'Manage all project drawings including versions, revisions, distribution, and approval tracking. Central repository for all CAD and design files.',
    howToUse: [
      'Upload drawings',
      'Track revisions and versions',
      'Distribute to team and contractors',
      'Log approvals and comments',
      'Maintain drawing register'
    ],
    relatedTo: [
      { feature: 'Plans (Takeoff)', why: 'Source drawings' },
      { feature: 'RFIs', why: 'Drawing clarifications' }
    ],
    nextStep: { action: 'Upload drawing', event: 'uploadDrawing' }
  },
  'hub-rfis': {
    title: 'RFIs & Submittals',
    definition: 'Manage Requests for Information (RFIs) and contractor submittals. Track questions, approvals, and documentation required during construction.',
    howToUse: [
      'Create new RFI',
      'Track RFI responses',
      'Review submittals',
      'Approve or reject materials',
      'Maintain submittal log'
    ],
    relatedTo: [
      { feature: 'Drawing Control', why: 'RFIs reference drawings' },
      { feature: 'Team', why: 'Assigned to contractors' }
    ],
    nextStep: { action: 'Create RFI', event: 'createRFI' }
  },
  'hub-scope-builder': {
    title: 'Scope Builder',
    definition: 'Build detailed scope of work documents by room and category. Define work items, specifications, and exclusions for proposals and contracts.',
    howToUse: [
      'Add rooms and areas',
      'Define work items per room',
      'Add specifications and details',
      'Note exclusions and allowances',
      'Generate scope document'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Scope drives estimates' },
      { feature: 'Clips', why: 'Link clips to scope items' }
    ],
    nextStep: { action: 'Add scope item', event: 'addScopeItem' }
  },
  'hub-documents-section': {
    title: 'Documents Section',
    definition: 'Project documentation including estimates and file management.',
    howToUse: ['Create estimates', 'Manage project files'],
    relatedTo: [{ feature: 'Estimates', why: 'Cost documentation' }, { feature: 'Files', why: 'File storage' }]
  },
  'hub-estimates': {
    title: 'Estimates',
    definition: 'Cost estimating tool for creating detailed project estimates from clips, takeoffs, and scope items. Generate professional estimate documents.',
    howToUse: [
      'Create line item estimates',
      'Import from clips and takeoffs',
      'Apply markup and labor rates',
      'Generate estimate PDF',
      'Convert to invoice when approved'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Clip prices to estimates' },
      { feature: 'Invoices', why: 'Estimates become invoices' }
    ],
    nextStep: { action: 'Create estimate', event: 'createEstimate' }
  },
  'hub-files': {
    title: 'Project Files',
    definition: 'File storage and management for all project documents, contracts, photos, and reference materials organized by category.',
    howToUse: [
      'Upload files and documents',
      'Organize by folder/category',
      'Search and filter files',
      'Share with team and clients',
      'Track file versions'
    ],
    relatedTo: [
      { feature: 'Documents', why: 'Central file storage' },
      { feature: 'Portals', why: 'Share via portal' }
    ],
    nextStep: { action: 'Upload file', event: 'uploadFile' }
  },
  'hub-financials-section': {
    title: 'Financials Section',
    definition: 'Complete financial management including job costing, contractor bids, purchase orders, invoices, and change orders.',
    howToUse: ['Track costs', 'Manage bids', 'Create POs', 'Send invoices'],
    relatedTo: [{ feature: 'Job Costing', why: 'Cost tracking' }, { feature: 'Invoices', why: 'Billing' }]
  },
  'hub-job-costing': {
    title: 'Job Costing',
    definition: 'Track all project costs vs budget. View spending by category, monitor profit margins, and compare actual costs to estimates.',
    howToUse: [
      'View cost breakdown by category',
      'Compare budget vs actual',
      'Track profit margins',
      'Identify cost overruns',
      'Generate cost reports'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Budget from estimates' },
      { feature: 'Purchase Orders', why: 'Costs from POs' }
    ],
    nextStep: { action: 'View cost report', event: 'viewCostReport' }
  },
  'hub-bids': {
    title: 'Contractor Bids',
    definition: 'Manage contractor bid packages, submissions, and comparisons. Send bid requests, collect responses, and select winning bids.',
    howToUse: [
      'Create bid packages',
      'Invite contractors to bid',
      'Review bid submissions',
      'Compare bids side-by-side',
      'Award contracts'
    ],
    relatedTo: [
      { feature: 'Contractors', why: 'Invite contractors' },
      { feature: 'Purchase Orders', why: 'Create PO from bid' }
    ],
    nextStep: { action: 'Create bid package', event: 'createBidPackage' }
  },
  'hub-purchase-orders': {
    title: 'Purchase Orders',
    definition: 'Create and manage purchase orders for materials, furnishings, and contractor work. Track order status, deliveries, and payments.',
    howToUse: [
      'Create PO from clips or bids',
      'Send to vendors',
      'Track order status',
      'Log deliveries received',
      'Match to invoices'
    ],
    relatedTo: [
      { feature: 'Clips', why: 'Order clipped items' },
      { feature: 'Vendors', why: 'Vendor management' }
    ],
    nextStep: { action: 'Create PO', event: 'createPO' }
  },
  'hub-invoices': {
    title: 'Invoices',
    definition: 'Create and send client invoices. Track payments, manage billing schedules, and maintain invoice history.',
    howToUse: [
      'Create invoice from estimate',
      'Add line items',
      'Send to client',
      'Track payment status',
      'Record payments received'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Convert to invoice' },
      { feature: 'Job Costing', why: 'Revenue tracking' }
    ],
    nextStep: { action: 'Create invoice', event: 'createInvoice' }
  },
  'hub-change-orders': {
    title: 'Change Orders',
    definition: 'Document and track project changes including scope modifications, cost adjustments, and schedule impacts. Get client approval and update budgets.',
    howToUse: [
      'Create change order',
      'Document scope change',
      'Calculate cost impact',
      'Note schedule impact',
      'Get client signature'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Cost adjustments' },
      { feature: 'Journey', why: 'Schedule impacts' }
    ],
    nextStep: { action: 'Create change order', event: 'createChangeOrder' }
  },
  'hub-communication-section': {
    title: 'Communication Section',
    definition: 'Project communication tools including messages, activity logs, and client portals.',
    howToUse: ['Send messages', 'View activity', 'Manage client access'],
    relatedTo: [{ feature: 'Messages', why: 'Team chat' }, { feature: 'Portals', why: 'Client access' }]
  },
  'hub-messages': {
    title: 'Project Messages',
    definition: 'Internal messaging and notes for project team communication. Leave notes, tag team members, and track conversations.',
    howToUse: [
      'Add project notes',
      'Tag team members',
      'Reply to messages',
      'Pin important notes',
      'Search message history'
    ],
    relatedTo: [
      { feature: 'Team', why: 'Team members' },
      { feature: 'Activity', why: 'Messages logged' }
    ],
    nextStep: { action: 'Add message', event: 'addMessage' }
  },
  'hub-activity': {
    title: 'Project Activity',
    definition: 'Complete activity log showing all project events, changes, and updates with timestamps and user attribution.',
    howToUse: [
      'View all project activity',
      'Filter by date or type',
      'See who made changes',
      'Track document views',
      'Monitor team engagement'
    ],
    relatedTo: [
      { feature: 'Progress', why: 'Progress entries' },
      { feature: 'Messages', why: 'Communication logged' }
    ],
    nextStep: { action: 'Filter activity', event: 'filterActivity' }
  },
  'hub-portals': {
    title: 'Client Portals',
    definition: 'Manage client access to project information through secure portals. Control what clients can see and share specific documents or selections.',
    howToUse: [
      'Create client portal link',
      'Control visible sections',
      'Share mood boards and selections',
      'Allow document uploads',
      'Track client activity'
    ],
    relatedTo: [
      { feature: 'Moods', why: 'Share mood boards' },
      { feature: 'Documents', why: 'Share documents' }
    ],
    nextStep: { action: 'Configure portal', event: 'configurePortal' }
  },

  // ============================================
  // PROJECT HUB - ADDITIONAL ITEMS
  // ============================================
  'hub-schedule': {
    title: 'Project Schedule',
    definition: 'Gantt chart and timeline view for tracking project milestones, deadlines, and dependencies. Coordinate tasks across team members and phases.',
    howToUse: [
      'View project timeline',
      'Add milestones and deadlines',
      'Set task dependencies',
      'Track critical path',
      'Export schedule for sharing'
    ],
    relatedTo: [
      { feature: 'Journey', why: 'Journey phases on timeline' },
      { feature: 'Actions', why: 'Task scheduling' }
    ],
    nextStep: { action: 'Add milestone', event: 'addMilestone' }
  },
  'hub-meetings': {
    title: 'Meeting Notes',
    definition: 'Document and organize meeting notes with voice recording support. Track decisions, action items, and attendees from project meetings.',
    howToUse: [
      'Create meeting notes',
      'Record audio for transcription',
      'Tag attendees',
      'Link action items to meetings',
      'Search past meeting notes'
    ],
    relatedTo: [
      { feature: 'Actions', why: 'Meeting action items' },
      { feature: 'Team', why: 'Meeting attendees' }
    ],
    nextStep: { action: 'Create meeting notes', event: 'createMeeting' }
  },
  'hub-pre-engineering': {
    title: 'Pre-Engineering Tools',
    definition: 'Calculation tools for early design phases including HVAC loads, structural estimates, plumbing requirements, and other pre-engineering calculations.',
    howToUse: [
      'Calculate HVAC loads',
      'Estimate structural requirements',
      'Size electrical systems',
      'Determine plumbing needs',
      'Generate calculation reports'
    ],
    relatedTo: [
      { feature: 'Estimates', why: 'Engineering cost estimates' },
      { feature: 'Scope Builder', why: 'Engineering scope items' }
    ],
    nextStep: { action: 'Run calculation', event: 'runCalculation' }
  },
  'hub-quality-section': {
    title: 'Quality Control Section',
    definition: 'Tools for tracking construction quality, site progress photos, inspections, and punch list management.',
    howToUse: ['Track site progress', 'Manage punch lists', 'Document inspections'],
    relatedTo: [{ feature: 'Site Progress', why: 'Progress documentation' }, { feature: 'Punch Lists', why: 'Issue tracking' }]
  },
  'hub-site-progress': {
    title: 'Site Progress',
    definition: 'Photo documentation and progress tracking for construction site visits. Capture work completed, document issues, and track project advancement.',
    howToUse: [
      'Upload progress photos',
      'Document completed work',
      'Note issues or concerns',
      'Compare before/after',
      'Generate progress reports'
    ],
    relatedTo: [
      { feature: 'Progress', why: 'Daily progress logs' },
      { feature: 'Photos', why: 'Site documentation' }
    ],
    nextStep: { action: 'Add progress photos', event: 'addProgressPhotos' }
  },
  'hub-punch-lists': {
    title: 'Punch Lists',
    definition: 'Track and manage items requiring attention before project closeout. Assign issues to contractors and track completion status.',
    howToUse: [
      'Create punch list items',
      'Assign to contractors',
      'Add photos of issues',
      'Track completion status',
      'Generate punch list report'
    ],
    relatedTo: [
      { feature: 'Team', why: 'Assign to contractors' },
      { feature: 'Quality Control', why: 'Issue resolution' }
    ],
    nextStep: { action: 'Add punch item', event: 'addPunchItem' }
  },
  'hub-file-control': {
    title: 'File Control',
    definition: 'Central document management for all project files including contracts, specs, photos, and correspondence organized by category.',
    howToUse: [
      'Upload and organize files',
      'Create folder structure',
      'Tag and categorize documents',
      'Control access permissions',
      'Track file versions'
    ],
    relatedTo: [
      { feature: 'Documents', why: 'Document storage' },
      { feature: 'Drawing Control', why: 'Drawing files' }
    ],
    nextStep: { action: 'Upload file', event: 'uploadFile' }
  },
  'hub-bid-packages': {
    title: 'Bid Packages',
    definition: 'Create and distribute bid packages to contractors. Package scope documents, drawings, and specifications for competitive bidding.',
    howToUse: [
      'Create bid package',
      'Attach drawings and specs',
      'Set bid due date',
      'Invite contractors to bid',
      'Manage bid submissions'
    ],
    relatedTo: [
      { feature: 'Bids', why: 'Bid responses' },
      { feature: 'Contractors', why: 'Invite contractors' }
    ],
    nextStep: { action: 'Create bid package', event: 'createBidPackage' }
  },
  'hub-received-invoices': {
    title: 'Received Invoices',
    definition: 'Track and manage invoices received from contractors and vendors. Match to purchase orders, approve for payment, and maintain invoice records.',
    howToUse: [
      'Upload received invoices',
      'Match to purchase orders',
      'Approve for payment',
      'Track payment status',
      'Dispute or reject invoices'
    ],
    relatedTo: [
      { feature: 'Purchase Orders', why: 'PO matching' },
      { feature: 'Job Costing', why: 'Cost tracking' }
    ],
    nextStep: { action: 'Upload invoice', event: 'uploadReceivedInvoice' }
  },
  'hub-draw-requests': {
    title: 'Draw Requests',
    definition: 'Manage construction draw requests for progress billing. Track work completed, calculate retainage, and generate draw request documents.',
    howToUse: [
      'Create draw request',
      'Document work completed',
      'Calculate retainage',
      'Get client approval',
      'Track payment history'
    ],
    relatedTo: [
      { feature: 'Progress', why: 'Work completed' },
      { feature: 'Invoices', why: 'Billing' }
    ],
    nextStep: { action: 'Create draw request', event: 'createDrawRequest' }
  }
};

export default function HelpIcon({ 
  elementId,
  title,
  definition,
  howToUse = [],
  relatedTo = [],
  nextStep,
  blueprintId,
  position = 'top-right', // top-right, top-left, bottom-right, bottom-left
  size = 'sm', // sm, md, lg
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const popoverRef = useRef(null);
  const navigate = useNavigate();
  const { fetchBlueprintById } = useBlueprint();
  const { helpIconsEnabled } = useHelpIcons();

  // Get content from database if elementId provided
  const content = elementId ? HELP_CONTENT[elementId] : null;
  const finalTitle = title || content?.title || 'Help';
  const finalDefinition = definition || content?.definition || '';
  const finalHowToUse = howToUse.length > 0 ? howToUse : (content?.howToUse || []);
  const finalRelatedTo = relatedTo.length > 0 ? relatedTo : (content?.relatedTo || []);
  const finalNextStep = nextStep || content?.nextStep;
  const finalBlueprintId = blueprintId || content?.blueprintId;

  // Calculate popover position
  useEffect(() => {
    if (isOpen && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const popoverWidth = 320;
      const popoverHeight = 400;
      
      let top = rect.bottom + 8;
      let left = rect.left - popoverWidth / 2 + rect.width / 2;
      
      // Adjust if off screen
      if (left < 16) left = 16;
      if (left + popoverWidth > window.innerWidth - 16) {
        left = window.innerWidth - popoverWidth - 16;
      }
      if (top + popoverHeight > window.innerHeight - 16) {
        top = rect.top - popoverHeight - 8;
      }
      
      setPopoverPosition({ top, left });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          iconRef.current && !iconRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Don't render if help icons are disabled - MUST be after all hooks
  if (!helpIconsEnabled) return null;

  const handleNextStep = () => {
    if (finalNextStep?.route) {
      navigate(finalNextStep.route);
      setIsOpen(false);
    } else if (finalNextStep?.event) {
      window.dispatchEvent(new CustomEvent(finalNextStep.event));
      setIsOpen(false);
    }
  };

  const handleRelatedClick = (item) => {
    if (item.route) {
      navigate(item.route);
      setIsOpen(false);
    }
  };

  const handleLearnMore = () => {
    if (finalBlueprintId) {
      fetchBlueprintById(finalBlueprintId);
      // Open compass to Learn tab
      window.dispatchEvent(new CustomEvent('openCompassLearn', { detail: { blueprintId: finalBlueprintId } }));
    }
    setIsOpen(false);
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const positionClasses = {
    'top-right': 'absolute -top-1 -right-1',
    'top-left': 'absolute -top-1 -left-1',
    'bottom-right': 'absolute -bottom-1 -right-1',
    'bottom-left': 'absolute -bottom-1 -left-1',
    'inline': 'inline-flex ml-1'
  };

  return (
    <>
      {/* Help Icon Button */}
      <button
        ref={iconRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`${positionClasses[position]} ${className} group z-10 p-0.5 rounded-full bg-white/80 hover:bg-white border border-gray-200 hover:border-[#BC5D41] shadow-sm hover:shadow transition-all`}
        title={`Help: ${finalTitle}`}
        data-testid={`help-icon-${elementId || 'generic'}`}
      >
        <HelpCircle className={`${sizeClasses[size]} text-gray-400 group-hover:text-[#BC5D41] transition-colors`} />
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed z-[10000] w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          style={{ top: popoverPosition.top, left: popoverPosition.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#70170F] to-[#BC5D41] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">{finalTitle}</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-[350px] overflow-y-auto">
            {/* Definition */}
            {finalDefinition && (
              <div>
                <p className="text-gray-700 text-sm leading-relaxed">{finalDefinition}</p>
              </div>
            )}

            {/* How to Use */}
            {finalHowToUse.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" />
                  How to Use
                </h4>
                <ul className="space-y-1.5">
                  {finalHowToUse.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#70170F]/10 text-[#70170F] text-xs flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Related Features */}
            {finalRelatedTo.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Link2 className="w-3 h-3" />
                  Connected To
                </h4>
                <div className="space-y-1">
                  {finalRelatedTo.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => handleRelatedClick(item)}
                      className="w-full text-left p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group/item"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800">{item.feature}</span>
                        {item.route && (
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-[#BC5D41] transition-colors" />
                        )}
                      </div>
                      {item.why && (
                        <span className="text-xs text-gray-500">{item.why}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Next Step */}
            {finalNextStep && (
              <button
                onClick={handleNextStep}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-[#70170F]/5 hover:bg-[#70170F]/10 border border-[#70170F]/20 transition-colors group/next"
              >
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-[#70170F]" />
                  <span className="text-sm font-medium text-[#70170F]">{finalNextStep.action}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#70170F]/50 group-hover/next:text-[#70170F] transition-colors" />
              </button>
            )}
          </div>

          {/* Footer */}
          {finalBlueprintId && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <button
                onClick={handleLearnMore}
                className="text-xs text-[#BC5D41] hover:underline flex items-center gap-1"
              >
                Learn more in Blueprint
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Wrapper component for cards that need help icons
export function HelpableCard({ children, elementId, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      {children}
      <HelpIcon elementId={elementId} />
    </div>
  );
}
